<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLogger } from 'vue-logger-plugin'
import { setStoreLogger } from '@/logger'
import { OrderDetailGet, OrderDetailGetListByPayee } from '@/api/Order'
import { GetSkuListBySearchWord, GetOrderTypeProductDetailBatch } from '@/api/Sku'
import { RmaCreate, RmaInboundSkuCreate, RmaInboundSkuDelete, RmaOutboundSkuCreate, RmaOutboundSkuDelete, RmaSubmit, RmaMasterGetList, RmaGet, RmaInboundPhotoCreate, RmaInboundPhotoDelete, RmaPaymentCreate, RmaPaymentDelete, RmaAvailablePaymentMethodGetList } from '@/api/RMA'
import { ConsultantGet } from '@/api/Consultant'
import type { IOrderDetailGetResponse, IOrderDetailGetItemSku, IOrderGetListResponse, IOrderListItem } from '@/types/OrderTypes'
import type { IAoeConsultant } from '@/types/ConsultantTypes'
import { Input as KInput, Checkbox as KCheckBox } from '@progress/kendo-vue-inputs'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { DropDownList, AutoComplete, type AutoCompleteChangeEvent } from '@progress/kendo-vue-dropdowns'
import { ExpansionPanel as KExpansionPanel, ExpansionPanelContent as KExpansionPanelContent } from '@progress/kendo-vue-layout'
import { Reveal as KReveal } from '@progress/kendo-vue-animation'
import { Loader as KLoader } from '@progress/kendo-vue-indicators'
import { trashIcon } from '@progress/kendo-svg-icons'
import type { UploadFileInfo } from '@progress/kendo-vue-upload'
import RMAPhotoUploader from './RMAPhotoUploader.vue'
import BraintreeRmaPaymentEntry from './PaymentGateways/Braintree/BraintreeRmaPaymentEntry.vue'
import BankPaymentRmaEntry from './PaymentGateways/BankPaymentRmaEntry.vue'
import NotSupported from './PaymentGateways/NotSupported.vue'
import { SetIsLoading } from '@/services/AoeHelpers'
import { handleError, AlertErrorResponse, AlertUnknownError } from '@/services/ErrorService'
import { SetupBraintreeForRmaPayment, BrainTreeControlFolderName, type IRmaPaymentViewModel } from '@/services/PaymentGateways/BraintreeRmaService.ts'
import { BankPaymentControlFolderName } from '@/services/PaymentGateways/BankPaymentService.ts'
import type { AoeMessageEvent, IErrorResponse } from '@/types/AoeTypes'
import Swal from 'sweetalert2'
import { PwsProfileAddressGet, PwsProfileAddressSave } from '@/api/AccountInfo'
import type { IAddress } from '@/types/CountryCultureTypes'
import type { IAddressVerificationListItem } from '@/types/CountryCultureTypes'
import { GetBeeNumberAuthenicatedUser, GetCountryCode, GetImageRootUrl } from '@/stores/SettingsStore'
import AddressControl from './PWS/AddressControl.vue'
import VerifyAddressPopup from './VerifyAddressPopup.vue'
import { Popup } from '@progress/kendo-vue-popup'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faArrowLeft, faPlus, faPencil } from '@fortawesome/free-solid-svg-icons'
import { FormatCurrency } from '@/services/currencyFormatter'
import { formatDate } from '@/api/Common'

const { t } = useI18n()
const logger = useLogger()
setStoreLogger(logger)

interface ReturnReason {
  code: string
  label: string
}

interface IAddressWithId extends IAddress {
  addressId: number
}

interface ExchangeSearchResult {
  skuName: string
  skuCode: string
  productName: string
  productAndSku: string
  fullProductImageUrl: string
}

interface ExchangeItem {
  skuCode: string
  skuName: string
  productName: string
  imageUrl: string
}

interface IRmaTotals {
  plannedInboundReturnTotal?: number
  feeTotal?: number
  plannedBalance?: number
  outboundTotal?: number
  outboundBuyingDiscountTotal?: number
  shippingAmount?: number
}

// Refund-method selection (self-service equivalent of the admin RMA screen's
// ReturnDetailPayment control, credit/refund branch only). Lets the customer pick which
// original order payment(s) a credit balance should be refunded to.
interface IOriginalPaymentMethod {
  paymentGuid: string
  name: string
  charge: number
  chargeNotRefunded: number
  checked: boolean
  refundAmount: number
  returnLineNumber: number | null
}

const returnReasons: ReturnReason[] = [
  { code: 'DUR', label: t('rma.returnReasons.DUR') },
  { code: 'DSR', label: t('rma.returnReasons.DSR') },
  { code: 'EXR', label: t('rma.returnReasons.EXR') }
]

const orderNumber = ref('')
const orderDetails = ref<IOrderDetailGetResponse | null>(null)
const orderListItem = ref<IOrderListItem | null>(null)
const consultantInfo = ref<IAoeConsultant | null>(null)
const selectedItems = ref<Set<number>>(new Set())
const selectedComponents = ref<Set<string>>(new Set()) // Format: "parentLineNumber-componentLineNumber"
const itemReasons = ref<Map<number, ReturnReason>>(new Map())
const componentReasons = ref<Map<string, ReturnReason>>(new Map()) // Format: "parentLineNumber-componentLineNumber"
const itemQuantities = ref<Map<number, number>>(new Map())
const componentQuantities = ref<Map<string, number>>(new Map()) // Format: "parentLineNumber-componentLineNumber"
const exchangeItems = ref<Map<number, ExchangeItem>>(new Map())
const componentExchangeItems = ref<Map<string, ExchangeItem>>(new Map()) // Format: "parentLineNumber-componentLineNumber"
const exchangeSearchText = ref<Map<number, string>>(new Map())
const componentExchangeSearchText = ref<Map<string, string>>(new Map()) // Format: "parentLineNumber-componentLineNumber"
const exchangeSearchResults = ref<Map<number, ExchangeSearchResult[]>>(new Map())
const componentExchangeSearchResults = ref<Map<string, ExchangeSearchResult[]>>(new Map()) // Format: "parentLineNumber-componentLineNumber"
const isSearchingExchange = ref<Map<number, boolean>>(new Map())
const componentIsSearchingExchange = ref<Map<string, boolean>>(new Map()) // Format: "parentLineNumber-componentLineNumber"
const exchangeSearchTimers = ref<Map<number, number>>(new Map())
const componentExchangeSearchTimers = ref<Map<string, number>>(new Map()) // Format: "parentLineNumber-componentLineNumber"
const itemPhotos = ref<Map<number, UploadFileInfo[]>>(new Map())
const itemDeletedPhotos = ref<Map<number, UploadFileInfo[]>>(new Map())
const componentPhotos = ref<Map<string, UploadFileInfo[]>>(new Map()) // Format: "parentLineNumber-componentLineNumber"
const componentDeletedPhotos = ref<Map<string, UploadFileInfo[]>>(new Map()) // Format: "parentLineNumber-componentLineNumber"
const itemPhotoEligibility = ref<Map<number, { isEligible: boolean, isRequired: boolean }>>(new Map())
const errorMessage = ref('')
const isSearching = ref(false)
const rmaCreated = ref(false)
const itemsToReceive = ref<Array<{ item: IOrderDetailGetItemSku, reason: ReturnReason, quantity: number }>>([])
const showAddressSelection = ref(false)
const existingRmaNumber = ref('')
const hasExistingRma = ref(false)
// Quantities already returned on prior RMAs for this order, keyed by order line (and by
// `${parentLineNumber}-${componentLineNumber}` for components), so partial returns still
// leave the remaining quantity available for a new RMA.
const returnedItemQuantities = ref<Map<number, number>>(new Map())
const returnedComponentQuantities = ref<Map<string, number>>(new Map())
const createdRmaNumber = ref('')
const rmaData = ref<unknown>(null)
const isSubmittingRMA = ref(false)

// Refund-method selection state
const originalPaymentMethods = ref<IOriginalPaymentMethod[]>([])
const isProcessingRefundSelection = ref(false)

// New-payment (BrainTree) state - collecting a balance owed by the customer, e.g. an
// exchange upcharge
const paymentViewModels = ref<IRmaPaymentViewModel[]>([])
const isLoadingPaymentMethods = ref(false)

// Address selection state
const addressList = ref<IAddressWithId[]>([])
const selectedAddressId = ref<number | null>(null)
const showAddressForm = ref(false)
const isLoadingAddresses = ref(false)
const addressVerificationShow = ref(false)
const verifyAddress = ref<IAddressVerificationListItem[]>([])
const addressVerifyError = ref(false)
const currentAddressId = ref<number | null>(null)
const savedScrollPosition = ref(0)
const addressForm = ref({
  firstName: '',
  lastName: '',
  address1: '',
  address2: '',
  houseNumber: '',
  city: '',
  state: '',
  postalCode: '',
  country: ''
})

// Non-reactive display ref for AddressControl name field to prevent space insertion bug
// See TEAU-969 fixes: d0783b0, 605ffb0
const addressNameDisplay = ref('')

// Ref to AddressControl component for validation
const addressControlRef = ref<InstanceType<typeof AddressControl> | null>(null)

logger.debug('AOERMAEntry.vue')

onMounted(async () => {
  // Check for orderid query parameter
  const urlParams = new URLSearchParams(window.location.search)
  const orderIdParam = urlParams.get('orderid')

  if (orderIdParam) {
    logger.debug('Found orderid in query string:', orderIdParam)
    orderNumber.value = orderIdParam
    // Automatically trigger search
    await searchOrder()
  }

  window.addEventListener('message', rmaPaymentMessageHandler)
})

onBeforeUnmount(() => {
  window.removeEventListener('message', rmaPaymentMessageHandler)
})

async function searchOrder() {
  if (!orderNumber.value.trim()) {
    errorMessage.value = t('rma.errors.enterOrderNumber')
    return
  }

  try {
    isSearching.value = true
    SetIsLoading(true)
    errorMessage.value = ''
    orderDetails.value = null
    selectedItems.value.clear()
    hasExistingRma.value = false
    existingRmaNumber.value = ''
    returnedItemQuantities.value = new Map()
    returnedComponentQuantities.value = new Map()

    const orderIdNumber = parseInt(orderNumber.value.trim())
    if (isNaN(orderIdNumber)) {
      errorMessage.value = t('rma.errors.enterValidOrderNumber')
      return
    }

    logger.debug('Searching for order:', orderIdNumber)

    // First, check if there are already submitted RMAs for this order so we can subtract
    // whatever quantities they already returned, rather than blocking the order outright.
    const rmaListResponse = await RmaMasterGetList({
      DatabaseFilter: {
        Where: {
          OperatorType: "And",
          Operands: [
            {
              Field: "AOEOrderId",
              FilterType: "Equal",
              Value: orderIdNumber
            },
            {
              OperatorType: "Or",
              Operands: [
                {
                  Field: "CddCodeGuidRmaStatus",
                  FilterType: "Equal",
                  Value: "48244328798903464625576089232"
                },
                {
                  Field: "CddCodeGuidRmaStatus",
                  FilterType: "Equal",
                  Value: "21996287234178881824122920074"
                }
              ]
            }
          ]
        }
      }
    })

    logger.debug('RMA list response:', rmaListResponse)

    if (rmaListResponse && rmaListResponse.items && rmaListResponse.items.length > 0) {
      const existingRmaNumbers: string[] = rmaListResponse.items
        .map((rma: { rmaNumber?: string }) => rma.rmaNumber)
        .filter((rmaNumber: string | undefined): rmaNumber is string => !!rmaNumber)

      existingRmaNumber.value = existingRmaNumbers.join(', ') || t('rma.NotAvailable')
      logger.debug('Found existing RMA(s):', existingRmaNumbers)

      // Fetch each existing RMA's returned skus so we know how much of each line is
      // still available to return
      const rmaDetailResponses = await Promise.all(
        existingRmaNumbers.map(rmaNumber => RmaGet(rmaNumber))
      )

      rmaDetailResponses.forEach(rmaDetail => {
        const inboundSkus = rmaDetail?.item?.inboundSkus
        if (!inboundSkus) return

        inboundSkus.forEach((inboundSku: { orderSkuLineNumber: number, orderSkuComponentLineNumber?: number, quantity: number }) => {
          const parentLineNumber = inboundSku.orderSkuLineNumber
          const componentLineNumber = inboundSku.orderSkuComponentLineNumber
          const quantity = inboundSku.quantity || 0

          if (componentLineNumber) {
            const key = `${parentLineNumber}-${componentLineNumber}`
            returnedComponentQuantities.value.set(key, (returnedComponentQuantities.value.get(key) || 0) + quantity)
          } else {
            returnedItemQuantities.value.set(parentLineNumber, (returnedItemQuantities.value.get(parentLineNumber) || 0) + quantity)
          }
        })
      })

      logger.debug('Returned item quantities:', returnedItemQuantities.value)
      logger.debug('Returned component quantities:', returnedComponentQuantities.value)
    }

    // Proceed with order search; whether it's fully blocked is determined once the
    // order details (and thus original line quantities) are loaded below
    const requestParams = {
      DatabaseFilter: {
        Where: {
          OperatorType: "And",
          Operands: [
            {
              Field: "AOEOrderId",
              FilterType: "Equal",
              Value: orderIdNumber
            }
          ]
        }
      }
    }
    // console.log('OrderDetailGetList request params:', requestParams)
    const listResponse: IOrderGetListResponse = await OrderDetailGetListByPayee(requestParams)

    logger.debug('Order list response:', listResponse)

    if (listResponse && listResponse.items && listResponse.items.length > 0) {
      const orderItem = listResponse.items[0]
      orderListItem.value = orderItem // Store the list item for customer info
      const aoeOrderIdToken = orderItem.aoeOrderIdToken

      logger.debug('Found order token:', aoeOrderIdToken)

      // Now get the full order details using the token
      const response = await OrderDetailGet(aoeOrderIdToken)

      logger.debug('Order details response:', response)

      if (response && response.item) {
        orderDetails.value = response

        // Block only if every sku/component on the order has already been fully returned
        hasExistingRma.value = isOrderFullyReturned(response.item.skus)

        if (hasExistingRma.value) {
          logger.debug('All items on this order have already been returned')
          return
        }

        // Fetch product details to check RMA photo eligibility flags
        await fetchProductPhotoEligibility(response.item.skus)

        // Fetch consultant information if beeNumberConsultant is available
        if (response.item.beeNumberConsultant) {
          try {
            const consultantResponse = await ConsultantGet(response.item.beeNumberConsultant)
            if (consultantResponse && consultantResponse.item) {
              consultantInfo.value = consultantResponse.item
              logger.debug('Consultant info loaded:', consultantInfo.value)
            }
          } catch (error) {
            logger.error('Error fetching consultant info:', error)
            // Don't show error to user, just log it
          }
        }
      } else {
        errorMessage.value = t('rma.errors.failedToLoadOrderDetails')
      }
    } else {
      errorMessage.value = t('rma.errors.orderNotFound')
    }
  } catch (error) {
    logger.error('Error searching for order:', error)
    errorMessage.value = t('rma.errors.failedToSearchOrder')
  } finally {
    isSearching.value = false
    SetIsLoading(false)
  }
}

async function fetchProductPhotoEligibility(skus: IOrderDetailGetItemSku[]) {
  try {
    // Get unique product codes from the SKUs
    const productCodes = [...new Set(skus.map(sku => sku.productCode))]

    if (productCodes.length === 0) {
      logger.debug('No product codes to fetch')
      return
    }

    logger.debug('Fetching product details for RMA photo eligibility:', productCodes)

    // Get order type ID from order details
    const orderTypeId = orderDetails.value?.item?.orderTypeId
    if (!orderTypeId) {
      logger.error('No orderTypeId available')
      return
    }

    // Fetch product details in batch
    const productDetails = await GetOrderTypeProductDetailBatch(orderTypeId, productCodes)

    if (!productDetails || productDetails.length === 0) {
      logger.debug('No product details returned')
      return
    }

    logger.debug('Product details response:', productDetails)

    // Create a map of productCode to photo eligibility flags
    const productPhotoFlags = new Map<string, { isEligible: boolean, isRequired: boolean }>()

    productDetails.forEach(detail => {
      if (detail?.item) {
        const product = detail.item as unknown as Record<string, unknown>
        const productCode = detail.item.productCode

        // Check for camelCase flags at product level (not in attributes)
        const isEligible = product.rmaPhotoEligible === true
        const isRequired = product.rmaSelfServePhotoRequired === true

        productPhotoFlags.set(productCode, { isEligible, isRequired })

        logger.debug(`Product ${productCode}: rmaPhotoEligible=${isEligible}, rmaSelfServePhotoRequired=${isRequired}`)
      }
    })

    // Map the flags to line numbers
    skus.forEach(sku => {
      const flags = productPhotoFlags.get(sku.productCode)
      if (flags) {
        itemPhotoEligibility.value.set(sku.lineNumber, flags)
      } else {
        // Default to not eligible if no flags found
        itemPhotoEligibility.value.set(sku.lineNumber, { isEligible: false, isRequired: false })
      }
    })

    logger.debug('Photo eligibility map:', itemPhotoEligibility.value)
  } catch (error) {
    logger.error('Error fetching product photo eligibility:', error)
    // Don't show error to user, just log it and continue
  }
}

function toggleItemSelection(lineNumber: number) {
  const item = orderDetails.value?.item.skus.find(sku => sku.lineNumber === lineNumber)

  if (selectedItems.value.has(lineNumber)) {
    selectedItems.value.delete(lineNumber)
  } else {
    selectedItems.value.add(lineNumber)

    // If parent is selected, deselect all its components
    if (item?.components && item.components.length > 0) {
      item.components.forEach(component => {
        const componentKey = `${lineNumber}-${component.componentLineNumber}`
        selectedComponents.value.delete(componentKey)
      })
    }
  }
}

function toggleComponentSelection(parentLineNumber: number, componentLineNumber: number) {
  const componentKey = `${parentLineNumber}-${componentLineNumber}`

  if (selectedComponents.value.has(componentKey)) {
    selectedComponents.value.delete(componentKey)
  } else {
    selectedComponents.value.add(componentKey)

    // If any component is selected, deselect the parent
    selectedItems.value.delete(parentLineNumber)
  }
}

function isItemSelected(lineNumber: number): boolean {
  return selectedItems.value.has(lineNumber)
}

function isComponentSelected(parentLineNumber: number, componentLineNumber: number): boolean {
  const componentKey = `${parentLineNumber}-${componentLineNumber}`
  return selectedComponents.value.has(componentKey)
}

function isItemDisabled(lineNumber: number): boolean {
  const item = orderDetails.value?.item.skus.find(sku => sku.lineNumber === lineNumber)
  if (!item) return false

  // Disable if this line has no remaining quantity left to return
  if (getRemainingQuantity(lineNumber, item.quantity) <= 0) {
    return true
  }

  // Disable parent if any of its components are selected
  if (item.components && item.components.length > 0) {
    return item.components.some(component =>
      isComponentSelected(lineNumber, component.componentLineNumber)
    )
  }
  return false
}

function isComponentDisabled(parentLineNumber: number, componentLineNumber: number): boolean {
  const item = orderDetails.value?.item.skus.find(sku => sku.lineNumber === parentLineNumber)
  const component = item?.components?.find(c => c.componentLineNumber === componentLineNumber)

  // Disable if this component has no remaining quantity left to return
  if (component && getRemainingComponentQuantity(parentLineNumber, componentLineNumber, component.quantity) <= 0) {
    return true
  }

  // Disable all components if parent is selected
  return isItemSelected(parentLineNumber)
}

function hasComponents(lineNumber: number): boolean {
  const item = orderDetails.value?.item.skus.find(sku => sku.lineNumber === lineNumber)
  return !!(item?.components && item.components.length > 0)
}

// Check if any components of a parent item are selected
function hasAnyComponentSelected(parentLineNumber: number): boolean {
  const item = orderDetails.value?.item.skus.find(sku => sku.lineNumber === parentLineNumber)
  if (!item?.components) return false

  return item.components.some(component => {
    const componentKey = `${parentLineNumber}-${component.componentLineNumber}`
    return selectedComponents.value.has(componentKey)
  })
}

function isPhotoEligible(lineNumber: number): boolean {
  return itemPhotoEligibility.value.get(lineNumber)?.isEligible ?? false
}

function isPhotoRequired(lineNumber: number): boolean {
  // Photos are not required for exchanges
  if (isExchangeReason(lineNumber)) {
    return false
  }
  return itemPhotoEligibility.value.get(lineNumber)?.isRequired ?? false
}

function getCustomerName(): string {
  // Try orderDetails first (has customer object), fallback to orderListItem
  const customer = orderDetails.value?.item?.customer || orderListItem.value?.customer
  if (!customer) return t('rma.NotAvailable')
  const { firstName, lastName } = customer
  const fullName = `${firstName || ''} ${lastName || ''}`.trim()
  if (!fullName) return t('rma.NotAvailable')

  // Add bee number if available
  const beeNumber = orderDetails.value?.item?.beeNumberCustomer
  return beeNumber ? `${fullName} (${beeNumber})` : fullName
}

function getCustomerEmail(): string {
  // Try orderDetails first (has customer object), fallback to orderListItem
  const customer = orderDetails.value?.item?.customer || orderListItem.value?.customer
  if (!customer?.email) return t('rma.NotAvailable')
  return customer.email
}

function getCustomerPhone(): string {
  // Try orderDetails first (has customer object), fallback to orderListItem
  const customer = orderDetails.value?.item?.customer || orderListItem.value?.customer
  if (!customer?.phone) return t('rma.NotAvailable')
  return customer.phone
}

function formatShippingAddress(): string {
  const addr = orderDetails.value?.item?.shippingAddress
  if (!addr) return t('rma.NotAvailable')

  const countryCode = addr.countryCode?.toUpperCase()

  // European address format (France, Germany, Austria)
  if (countryCode === 'FR' || countryCode === 'DE' || countryCode === 'AT') {
    const parts = [
      addr.addressLine1, // Street name
      addr.houseNumber, // House number
      addr.postalCode,
      addr.city
    ].filter(Boolean)
    return parts.join(', ') || t('rma.NotAvailable')
  }

  // US and other address formats
  const parts = [
    addr.addressLine1,
    addr.addressLine2,
    addr.city,
    addr.stateCode,
    addr.postalCode
  ].filter(Boolean)

  return parts.join(', ') || t('rma.NotAvailable')
}

function getRmaCount(): number {
  // Access rmaStatistics from orderDetails
  const rmaStats = orderDetails.value?.item?.rmaStatistics
  return rmaStats?.rmaCount ?? 0
}

function getConsultantName(): string {
  if (!consultantInfo.value) return t('rma.NotAvailable')
  const { firstName, lastName } = consultantInfo.value
  const fullName = `${firstName || ''} ${lastName || ''}`.trim()
  if (!fullName) return t('rma.NotAvailable')

  // Add bee number from order details (consultant and customer might be different)
  const beeNumber = orderDetails.value?.item?.beeNumberConsultant
  return beeNumber ? `${fullName} (${beeNumber})` : fullName
}

function setItemReason(lineNumber: number, reason: ReturnReason | null) {
  if (reason && reason.code) {
    itemReasons.value.set(lineNumber, reason)
  } else {
    itemReasons.value.delete(lineNumber)
  }
}

function getItemReason(lineNumber: number): ReturnReason | null {
  return itemReasons.value.get(lineNumber) || null
}

function getQuantityOptions(maxQuantity: number): number[] {
  return Array.from({ length: Math.max(maxQuantity, 0) }, (_, i) => i + 1)
}

// How much of a top-level order line is still available to return, after subtracting
// quantities already returned on prior (non-cancelled) RMAs for this order
function getRemainingQuantity(lineNumber: number, orderedQuantity: number): number {
  const alreadyReturned = returnedItemQuantities.value.get(lineNumber) || 0
  return Math.max(orderedQuantity - alreadyReturned, 0)
}

// Same as getRemainingQuantity, but for a component within a bundled sku
function getRemainingComponentQuantity(parentLineNumber: number, componentLineNumber: number, orderedQuantity: number): number {
  const key = `${parentLineNumber}-${componentLineNumber}`
  const alreadyReturned = returnedComponentQuantities.value.get(key) || 0
  return Math.max(orderedQuantity - alreadyReturned, 0)
}

// True only when every sku (and, for bundles, every component) on the order has no
// remaining quantity left to return
function isOrderFullyReturned(skus: IOrderDetailGetItemSku[]): boolean {
  return skus.every(sku => {
    const parentFullyReturned = getRemainingQuantity(sku.lineNumber, sku.quantity) <= 0
    if (sku.components && sku.components.length > 0) {
      const componentsFullyReturned = sku.components.every(component =>
        getRemainingComponentQuantity(sku.lineNumber, component.componentLineNumber, component.quantity) <= 0
      )
      return parentFullyReturned && componentsFullyReturned
    }
    return parentFullyReturned
  })
}

function setItemQuantity(lineNumber: number, quantity: number) {
  itemQuantities.value.set(lineNumber, quantity)
  logger.debug(`Item ${lineNumber} quantity set to:`, quantity)
}

function getItemQuantity(lineNumber: number, defaultQuantity: number): number {
  return itemQuantities.value.get(lineNumber) || defaultQuantity
}

// Exchange item functions
function isExchangeReason(lineNumber: number): boolean {
  const reason = itemReasons.value.get(lineNumber)
  return reason?.code === 'EXR'
}

function getExchangeItem(lineNumber: number): ExchangeItem | null {
  return exchangeItems.value.get(lineNumber) || null
}

function removeExchangeItem(lineNumber: number) {
  // Clear any pending search timer
  const existingTimer = exchangeSearchTimers.value.get(lineNumber)
  if (existingTimer) {
    clearTimeout(existingTimer)
    exchangeSearchTimers.value.delete(lineNumber)
  }

  exchangeItems.value.delete(lineNumber)
  exchangeSearchText.value.delete(lineNumber)
  exchangeSearchResults.value.delete(lineNumber)
}

async function handleExchangeSearch(lineNumber: number, event: AutoCompleteChangeEvent) {
  const searchValue = event.target.value

  logger.debug('handleExchangeSearch', lineNumber, searchValue, event)

  // Check if user selected an item from the dropdown (value will be the full "productAndSku" string)
  const currentResults = exchangeSearchResults.value.get(lineNumber) || []
  const selectedResult = currentResults.find(result => result.productAndSku === searchValue)

  if (selectedResult) {
    logger.debug('User selected an item from dropdown:', selectedResult)
    selectExchangeItem(lineNumber, selectedResult)
    return
  }

  exchangeSearchText.value.set(lineNumber, searchValue)

  // Clear any existing timer for this line number
  const existingTimer = exchangeSearchTimers.value.get(lineNumber)
  if (existingTimer) {
    clearTimeout(existingTimer)
  }

  // Clear button clicked
  if (event.event?.currentTarget?.className === 'k-clear-value') {
    exchangeSearchResults.value.delete(lineNumber)
    exchangeSearchTimers.value.delete(lineNumber)
    return
  }

  // Search requires at least 3 characters
  if (searchValue.length < 3) {
    exchangeSearchResults.value.delete(lineNumber)
    exchangeSearchTimers.value.delete(lineNumber)
    return
  }

  // Set up debounced search - wait 500ms after user stops typing
  const timer = setTimeout(async () => {
    isSearchingExchange.value.set(lineNumber, true)

    try {
      const orderTypeId = orderDetails.value?.item?.orderTypeId || ''
      const imageRootURL = await GetImageRootUrl()

      const response = await GetSkuListBySearchWord(orderTypeId, searchValue, 10)
      logger.debug('Exchange search results:', response)

      const results: ExchangeSearchResult[] = response.items.map(item => ({
        skuName: item.skuName,
        skuCode: item.skuCode,
        productName: item.product.productName,
        productAndSku: `${item.product.productName} - ${item.skuCode}`,
        fullProductImageUrl: `${imageRootURL}/catalogcontent/${item.product.attributes.CatalogImage}`
      }))

      exchangeSearchResults.value.set(lineNumber, results)
    } catch (error) {
      logger.error('Error searching for exchange items:', error)
      exchangeSearchResults.value.set(lineNumber, [])
    } finally {
      isSearchingExchange.value.set(lineNumber, false)
      exchangeSearchTimers.value.delete(lineNumber)
    }
  }, 500)

  exchangeSearchTimers.value.set(lineNumber, timer)
}

function selectExchangeItem(lineNumber: number, result: ExchangeSearchResult) {
  const exchangeItem: ExchangeItem = {
    skuCode: result.skuCode,
    skuName: result.skuName,
    productName: result.productName,
    imageUrl: result.fullProductImageUrl
  }

  logger.debug('Setting exchange item for line:', lineNumber, exchangeItem)
  exchangeItems.value.set(lineNumber, exchangeItem)
  exchangeSearchText.value.set(lineNumber, '')
  exchangeSearchResults.value.delete(lineNumber)

  logger.debug('Exchange item stored. Current map:', Array.from(exchangeItems.value.entries()))
  logger.debug('Has exchange item check:', exchangeItems.value.has(lineNumber))
}

function getExchangeSearchResults(lineNumber: number): ExchangeSearchResult[] {
  return exchangeSearchResults.value.get(lineNumber) || []
}

function getExchangeSearchText(lineNumber: number): string {
  return exchangeSearchText.value.get(lineNumber) || ''
}

// Component SKU helper functions
function setComponentReason(parentLineNumber: number, componentLineNumber: number, reason: ReturnReason | null) {
  const componentKey = `${parentLineNumber}-${componentLineNumber}`
  if (reason && reason.code) {
    componentReasons.value.set(componentKey, reason)
  } else {
    componentReasons.value.delete(componentKey)
  }
}

function getComponentReason(parentLineNumber: number, componentLineNumber: number): ReturnReason | null {
  const componentKey = `${parentLineNumber}-${componentLineNumber}`
  return componentReasons.value.get(componentKey) || null
}

function setComponentQuantity(parentLineNumber: number, componentLineNumber: number, quantity: number) {
  const componentKey = `${parentLineNumber}-${componentLineNumber}`
  componentQuantities.value.set(componentKey, quantity)
  logger.debug(`Component ${componentKey} quantity set to:`, quantity)
}

function getComponentQuantity(parentLineNumber: number, componentLineNumber: number, defaultQuantity: number): number {
  const componentKey = `${parentLineNumber}-${componentLineNumber}`
  return componentQuantities.value.get(componentKey) || defaultQuantity
}

function isComponentExchangeReason(parentLineNumber: number, componentLineNumber: number): boolean {
  const componentKey = `${parentLineNumber}-${componentLineNumber}`
  const reason = componentReasons.value.get(componentKey)
  return reason?.code === 'EXR'
}

function getComponentExchangeItem(parentLineNumber: number, componentLineNumber: number): ExchangeItem | null {
  const componentKey = `${parentLineNumber}-${componentLineNumber}`
  return componentExchangeItems.value.get(componentKey) || null
}

function removeComponentExchangeItem(parentLineNumber: number, componentLineNumber: number) {
  const componentKey = `${parentLineNumber}-${componentLineNumber}`

  // Clear any pending search timer
  const existingTimer = componentExchangeSearchTimers.value.get(componentKey)
  if (existingTimer) {
    clearTimeout(existingTimer)
    componentExchangeSearchTimers.value.delete(componentKey)
  }

  componentExchangeItems.value.delete(componentKey)
  componentExchangeSearchText.value.delete(componentKey)
  componentExchangeSearchResults.value.delete(componentKey)
}

async function handleComponentExchangeSearch(parentLineNumber: number, componentLineNumber: number, event: AutoCompleteChangeEvent) {
  const componentKey = `${parentLineNumber}-${componentLineNumber}`
  const searchValue = event.target.value

  logger.debug('handleComponentExchangeSearch', componentKey, searchValue, event)

  // Check if user selected an item from the dropdown
  const currentResults = componentExchangeSearchResults.value.get(componentKey) || []
  const selectedResult = currentResults.find(result => result.productAndSku === searchValue)

  if (selectedResult) {
    logger.debug('User selected an item from dropdown:', selectedResult)
    selectComponentExchangeItem(parentLineNumber, componentLineNumber, selectedResult)
    return
  }

  componentExchangeSearchText.value.set(componentKey, searchValue)

  // Clear any existing timer
  const existingTimer = componentExchangeSearchTimers.value.get(componentKey)
  if (existingTimer) {
    clearTimeout(existingTimer)
  }

  // Clear button clicked
  if (event.event?.currentTarget?.className === 'k-clear-value') {
    componentExchangeSearchResults.value.delete(componentKey)
    componentExchangeSearchTimers.value.delete(componentKey)
    return
  }

  // Search requires at least 3 characters
  if (searchValue.length < 3) {
    componentExchangeSearchResults.value.delete(componentKey)
    componentExchangeSearchTimers.value.delete(componentKey)
    return
  }

  // Set up debounced search
  const timer = setTimeout(async () => {
    componentIsSearchingExchange.value.set(componentKey, true)

    try {
      const orderTypeId = orderDetails.value?.item?.orderTypeId || ''
      const imageRootURL = await GetImageRootUrl()

      const response = await GetSkuListBySearchWord(orderTypeId, searchValue, 10)
      logger.debug('Component exchange search results:', response)

      const results: ExchangeSearchResult[] = response.items.map(item => ({
        skuName: item.skuName,
        skuCode: item.skuCode,
        productName: item.product.productName,
        productAndSku: `${item.product.productName} - ${item.skuCode}`,
        fullProductImageUrl: `${imageRootURL}/catalogcontent/${item.product.attributes.CatalogImage}`
      }))

      componentExchangeSearchResults.value.set(componentKey, results)
    } catch (error) {
      logger.error('Error searching for component exchange items:', error)
      componentExchangeSearchResults.value.set(componentKey, [])
    } finally {
      componentIsSearchingExchange.value.set(componentKey, false)
      componentExchangeSearchTimers.value.delete(componentKey)
    }
  }, 500)

  componentExchangeSearchTimers.value.set(componentKey, timer)
}

function selectComponentExchangeItem(parentLineNumber: number, componentLineNumber: number, result: ExchangeSearchResult) {
  const componentKey = `${parentLineNumber}-${componentLineNumber}`
  const exchangeItem: ExchangeItem = {
    skuCode: result.skuCode,
    skuName: result.skuName,
    productName: result.productName,
    imageUrl: result.fullProductImageUrl
  }

  logger.debug('Setting component exchange item for:', componentKey, exchangeItem)
  componentExchangeItems.value.set(componentKey, exchangeItem)
  componentExchangeSearchText.value.set(componentKey, '')
  componentExchangeSearchResults.value.delete(componentKey)
}

function getComponentExchangeSearchResults(parentLineNumber: number, componentLineNumber: number): ExchangeSearchResult[] {
  const componentKey = `${parentLineNumber}-${componentLineNumber}`
  return componentExchangeSearchResults.value.get(componentKey) || []
}

function getComponentExchangeSearchText(parentLineNumber: number, componentLineNumber: number): string {
  const componentKey = `${parentLineNumber}-${componentLineNumber}`
  return componentExchangeSearchText.value.get(componentKey) || ''
}

function getComponentPhotos(parentLineNumber: number, componentLineNumber: number): UploadFileInfo[] {
  const componentKey = `${parentLineNumber}-${componentLineNumber}`
  return componentPhotos.value.get(componentKey) || []
}

function setComponentPhotos(parentLineNumber: number, componentLineNumber: number, files: UploadFileInfo[]) {
  const componentKey = `${parentLineNumber}-${componentLineNumber}`
  componentPhotos.value.set(componentKey, files)
}

function getComponentDeletedPhotos(parentLineNumber: number, componentLineNumber: number): UploadFileInfo[] {
  const componentKey = `${parentLineNumber}-${componentLineNumber}`
  return componentDeletedPhotos.value.get(componentKey) || []
}

function setComponentDeletedPhotos(parentLineNumber: number, componentLineNumber: number, files: UploadFileInfo[]) {
  const componentKey = `${parentLineNumber}-${componentLineNumber}`
  componentDeletedPhotos.value.set(componentKey, files)
}

async function continueToAddress() {
  // Load address list and show address display
  await loadAddressList()

  // Try to match the shipping address from order details with an address in the list
  if (orderDetails.value?.item?.shippingAddress) {
    const shippingAddr = orderDetails.value.item.shippingAddress

    // Find matching address by comparing key fields
    const matchingAddress = addressList.value.find(addr =>
      addr.addressLine1 === shippingAddr.addressLine1 &&
      addr.city === shippingAddr.city &&
      addr.postalCode === shippingAddr.postalCode
    )

    if (matchingAddress) {
      selectedAddressId.value = matchingAddress.addressId
      logger.debug('Auto-selected shipping address from order:', matchingAddress)
    } else {
      // Shipping address not in saved addresses - add it to the list for display
      const tempAddressId = -1 // Use negative ID to indicate it's from order, not saved

      // Type assertion: OrderTypes.IAddress doesn't have stateCode/countryCode in its definition
      // but the actual API response includes them
      type ShippingAddressWithExtras = typeof shippingAddr & { stateCode?: string; countryCode?: string }
      const extendedAddr = shippingAddr as ShippingAddressWithExtras

      const orderShippingAddress: IAddressWithId = {
        addressId: tempAddressId,
        firstName: shippingAddr.firstName || null,
        lastName: shippingAddr.lastName || null,
        addressLine1: shippingAddr.addressLine1,
        addressLine2: shippingAddr.addressLine2 || null,
        houseNumber: shippingAddr.houseNumber || null,
        city: shippingAddr.city,
        stateCode: extendedAddr.stateCode || null,
        postalCode: shippingAddr.postalCode,
        countryCode: extendedAddr.countryCode || ''
      }

      // Add to the beginning of the address list
      addressList.value.unshift(orderShippingAddress)
      selectedAddressId.value = tempAddressId
      logger.debug('Added shipping address from order to list:', orderShippingAddress)
    }
  } else if (addressList.value.length > 0 && !selectedAddressId.value) {
    // Fallback: select first address if no shipping address in order
    selectedAddressId.value = addressList.value[0].addressId
    logger.debug('No shipping address in order, selected first address')
  }

  rmaCreated.value = true
  logger.debug('Moving to address step')
}

function goBack() {
  logger.debug('Going back to previous page')
  window.history.back()
}

async function goBackToItems() {
  // The RMA can't simply be deleted and recreated once it has line items/payments on it
  // (RMADelete rejects it), so instead clear its inbound/outbound sku and payment lines and
  // keep the RMA itself - proceedWithReturn() will add fresh lines back onto this same RMA
  // number if the user changes their selection and proceeds again.
  if (createdRmaNumber.value) {
    const rmaNumber = createdRmaNumber.value

    try {
      SetIsLoading(true)
      logger.debug('Clearing existing lines for RMA:', rmaNumber)

      const currentRma = await RmaGet(rmaNumber)
      const item = currentRma?.item as {
        inboundSkus?: Array<{ lineNumber: number }>
        outboundSkus?: Array<{ lineNumber: number }>
        payments?: Array<{ lineNumber: number }>
      } | undefined

      // Payments are removed first since they're calculated off the current sku totals
      await Promise.all(
        (item?.payments || []).map(payment => RmaPaymentDelete(rmaNumber, payment.lineNumber))
      )

      await Promise.all([
        ...(item?.inboundSkus || []).map(sku => RmaInboundSkuDelete(rmaNumber, sku.lineNumber)),
        ...(item?.outboundSkus || []).map(sku => RmaOutboundSkuDelete(rmaNumber, sku.lineNumber)),
      ])

      logger.debug('Existing lines cleared; RMA will be reused:', rmaNumber)
    } catch (error) {
      logger.error('Error clearing existing RMA lines:', error)
      // Continue with going back even if clearing fails
    } finally {
      SetIsLoading(false)
    }
  }

  rmaCreated.value = false
  showAddressSelection.value = false
  originalPaymentMethods.value = []
  paymentViewModels.value = []
  logger.debug('Going back to item selection')
}

function resetForm() {
  // Reset all state variables to initial values
  orderNumber.value = ''
  orderDetails.value = null
  selectedItems.value.clear()
  itemReasons.value.clear()
  itemQuantities.value.clear()
  errorMessage.value = ''
  isSearching.value = false
  rmaCreated.value = false
  itemsToReceive.value = []
  showAddressSelection.value = false
  existingRmaNumber.value = ''
  hasExistingRma.value = false
  returnedItemQuantities.value.clear()
  returnedComponentQuantities.value.clear()
  createdRmaNumber.value = ''
  rmaData.value = null
  selectedAddressId.value = null
  showAddressForm.value = false
  itemPhotos.value.clear()
  itemDeletedPhotos.value.clear()
  isSubmittingRMA.value = false
  originalPaymentMethods.value = []
  isProcessingRefundSelection.value = false
  paymentViewModels.value = []
  isLoadingPaymentMethods.value = false

  // Clear any pending exchange-search debounce timers before dropping the maps, then reset
  // exchange item selection state - otherwise the next RMA's "select exchange item" section
  // starts out pre-populated with the previous RMA's selections (line numbers are reused
  // across RMAs, so stale entries keyed by lineNumber would appear to carry over)
  exchangeSearchTimers.value.forEach((timerId) => window.clearTimeout(timerId))
  componentExchangeSearchTimers.value.forEach((timerId) => window.clearTimeout(timerId))
  exchangeItems.value.clear()
  componentExchangeItems.value.clear()
  exchangeSearchText.value.clear()
  componentExchangeSearchText.value.clear()
  exchangeSearchResults.value.clear()
  componentExchangeSearchResults.value.clear()
  isSearchingExchange.value.clear()
  componentIsSearchingExchange.value.clear()
  exchangeSearchTimers.value.clear()
  componentExchangeSearchTimers.value.clear()

  logger.debug('Form reset to initial state')
}

// Photo management helper functions
function getItemPhotos(lineNumber: number): UploadFileInfo[] {
  return itemPhotos.value.get(lineNumber) || []
}

function setItemPhotos(lineNumber: number, files: UploadFileInfo[]) {
  itemPhotos.value.set(lineNumber, files)
}

function getItemDeletedPhotos(lineNumber: number): UploadFileInfo[] {
  return itemDeletedPhotos.value.get(lineNumber) || []
}

function setItemDeletedPhotos(lineNumber: number, files: UploadFileInfo[]) {
  itemDeletedPhotos.value.set(lineNumber, files)
}

// Computed property to get the selected address
const selectedAddress = computed(() => {
  if (!selectedAddressId.value) return null
  return addressList.value.find(addr => addr.addressId === selectedAddressId.value) || null
})

// Totals breakdown for the RMA being created, sourced from the RmaGet response fetched
// right after RmaCreate (mirrors the "Totals" panel shown on the admin RMA screen)
const rmaTotals = computed<IRmaTotals | null>(() => {
  return (rmaData.value as { item?: IRmaTotals } | null)?.item ?? null
})

// True when the RMA results in a credit owed back to the customer. Note: plannedBalance
// uses the opposite sign convention from the admin RMA screen's `dataItem.total` (positive
// plannedBalance = credit due to the customer; negative = customer owes money), so this is
// intentionally the reverse of what a naive port of the admin `total > 0` check would give.
const refundRequired = computed(() => (rmaTotals.value?.plannedBalance ?? 0) > 0)

// True when the RMA results in a balance owed by the customer (e.g. an exchange upcharge)
const paymentRequired = computed(() => (rmaTotals.value?.plannedBalance ?? 0) < 0)

// Signed "Total" for the totals breakdown - negative for a refund (credit owed back to the
// customer), positive for a charge (customer owes more). This is plannedBalance with its
// sign flipped (see the refundRequired note above); the explicit zero check avoids negating
// zero into "-0", which some locales would otherwise render as "-$0.00".
const rmaNetTotal = computed(() => {
  const balance = rmaTotals.value?.plannedBalance ?? 0
  return balance === 0 ? 0 : -balance
})

// How much of the credit still needs to be allocated to a refund method
const remainingRefundToAllocate = computed(() => {
  const totalCredit = Math.abs(rmaTotals.value?.plannedBalance ?? 0)
  const allocated = originalPaymentMethods.value.reduce(
    (sum, payment) => sum + (payment.checked ? payment.refundAmount || 0 : 0),
    0
  )
  return Math.max(totalCredit - allocated, 0)
})

// Rebuilds the refund-method selection list from the latest RMA detail (original order
// payments + any refund selections already made on this RMA)
function refreshOriginalPaymentMethods() {
  const item = (rmaData.value as { item?: { order?: { payments?: unknown[] }, payments?: unknown[] } } | null)?.item
  const orderPayments = (item?.order?.payments || []) as Array<{ paymentGuid: string, name: string, charge: number, chargeRefunded?: number }>
  const returnPayments = (item?.payments || []) as Array<{ paymentGuidRelated?: string, rmaEnteredRefund?: number, lineNumber: number }>

  originalPaymentMethods.value = orderPayments.map((payment) => {
    const existingReturnPayment = returnPayments.find(
      (rp) => rp.paymentGuidRelated === payment.paymentGuid
    )

    return {
      paymentGuid: payment.paymentGuid,
      name: payment.name,
      charge: payment.charge,
      chargeNotRefunded: payment.charge - (payment.chargeRefunded || 0),
      checked: !!existingReturnPayment,
      refundAmount: existingReturnPayment ? (existingReturnPayment.rmaEnteredRefund || 0) * -1 : 0,
      returnLineNumber: existingReturnPayment ? existingReturnPayment.lineNumber : null
    }
  })
}

async function checkedChangeHandler(payment: IOriginalPaymentMethod) {
  payment.checked = !payment.checked

  if (!payment.checked) {
    payment.refundAmount = 0

    if (payment.returnLineNumber) {
      try {
        isProcessingRefundSelection.value = true
        const response = await RmaPaymentDelete(createdRmaNumber.value, payment.returnLineNumber)
        rmaData.value = response
        refreshOriginalPaymentMethods()
      } catch (error) {
        logger.error('Error removing refund selection:', error)
        errorMessage.value = t('rma.errors.failedToUpdateRefund')
      } finally {
        isProcessingRefundSelection.value = false
      }
    }
    return
  }

  const otherCheckedChargeNotRefunded = originalPaymentMethods.value
    .filter((p) => p !== payment && p.checked)
    .reduce((sum, p) => sum + p.chargeNotRefunded, 0)

  const availableAmount = Math.abs(rmaTotals.value?.plannedBalance ?? 0) - otherCheckedChargeNotRefunded
  payment.refundAmount = Math.min(payment.chargeNotRefunded, Math.max(availableAmount, 0))

  if (payment.refundAmount > 0) {
    try {
      isProcessingRefundSelection.value = true
      const response = await RmaPaymentCreate(createdRmaNumber.value, payment.paymentGuid, payment.refundAmount * -1)
      rmaData.value = response
      refreshOriginalPaymentMethods()
    } catch (error) {
      logger.error('Error selecting refund method:', error)
      errorMessage.value = t('rma.errors.failedToUpdateRefund')
    } finally {
      isProcessingRefundSelection.value = false
    }
  }
}

// Existing payments recorded against this RMA (either refund selections or new charges,
// which are mutually exclusive depending on whether the planned balance is a credit or an
// amount owed)
const rmaPayments = computed(() => {
  const item = (rmaData.value as { item?: { payments?: unknown[] } } | null)?.item
  return (item?.payments || []) as Array<{
    lineNumber: number
    name: string
    charge: number
    transactionStateText: string
    paymentGuidRelated?: string
    rmaEnteredRefund?: number
  }>
})

// Refund-type payment records leave `charge` at 0 by design and store the actual amount in
// `rmaEnteredRefund` instead - display whichever field actually holds the real amount.
// rmaEnteredRefund is kept negative (money going back to the customer, not being charged
// again), so it's shown as-is rather than absolute-valued.
function getPaymentDisplayAmount(payment: { charge: number, paymentGuidRelated?: string, rmaEnteredRefund?: number }): number {
  return payment.paymentGuidRelated ? (payment.rmaEnteredRefund ?? 0) : payment.charge
}

const paymentMethodsExpanded = computed(() =>
  paymentViewModels.value.map((viewModel) => viewModel.expanded === true)
)

function closeAllPaymentPanels() {
  paymentViewModels.value.forEach((viewModel) => {
    viewModel.expanded = false
  })
}

function paymentExpansionPanelHandler(index: number) {
  const currentValue = paymentMethodsExpanded.value[index]
  if (currentValue) {
    paymentViewModels.value[index].expanded = false
  } else {
    closeAllPaymentPanels()
    paymentViewModels.value[index].expanded = true
  }
}

// Refetches the RMA and rebuilds dependent view state - used after a new payment is
// created via the BrainTree/PayPal gateway flow (which posts directly to the connector,
// bypassing the normal RmaPaymentCreate wrapper), mirroring the admin RMA screen's
// ReturnDetailPayment refresh-on-postMessage behavior.
async function refreshRmaData() {
  if (!createdRmaNumber.value) {
    return
  }

  try {
    const response = await RmaGet(createdRmaNumber.value)
    rmaData.value = response
    refreshOriginalPaymentMethods()
  } catch (error) {
    logger.error('Error refreshing RMA data:', error)
  }
}

// Loads the available payment methods for collecting a balance owed by the customer and
// sets up the BrainTree/PayPal gateway (self-service equivalent of the admin RMA screen's
// v-if (dataItem.total > 0) branch in ReturnDetailPayment.vue)
async function loadAvailablePaymentMethodsForCharge() {
  const rmaNumber = createdRmaNumber.value
  // plannedBalance is negative when the customer owes money; the gateway needs a positive charge
  const charge = Math.abs(rmaTotals.value?.plannedBalance ?? 0)

  if (!rmaNumber || !paymentRequired.value || rmaPayments.value.length > 0) {
    return
  }

  try {
    isLoadingPaymentMethods.value = true
    const response = await RmaAvailablePaymentMethodGetList(rmaNumber)

    paymentViewModels.value = (response.items || []).map((paymentMethod: {
      name: string
      systemKeyword: string
      controlFolderName: string
      parameters?: Record<string, unknown>
    }) => ({
      name: paymentMethod.name,
      systemKeyword: paymentMethod.systemKeyword,
      controlFolderName: paymentMethod.controlFolderName,
      expanded: false,
      parameters: paymentMethod.parameters,
    }))

    if (paymentViewModels.value.length > 0) {
      paymentViewModels.value[0].expanded = true
    }

    const currencyId = (rmaData.value as { item?: { order?: { currencyId?: string } } } | null)
      ?.item?.order?.currencyId || 'USD'

    await SetupBraintreeForRmaPayment(t, rmaNumber, charge, currencyId, paymentViewModels.value)
  } catch (error) {
    logger.error('Error loading available payment methods:', error)
    errorMessage.value = t('rma.errors.failedToLoadPaymentMethods')
  } finally {
    isLoadingPaymentMethods.value = false
  }
}

async function deleteRmaPayment(payment: { lineNumber: number }) {
  try {
    const response = await RmaPaymentDelete(createdRmaNumber.value, payment.lineNumber)
    rmaData.value = response
    refreshOriginalPaymentMethods()
    await loadAvailablePaymentMethodsForCharge()
  } catch (error) {
    logger.error('Error deleting payment:', error)
    errorMessage.value = t('rma.errors.failedToUpdateRefund')
  }
}

async function rmaPaymentMessageHandler(e: AoeMessageEvent) {
  const allowedOrigin = `${window.location.protocol}//${window.location.hostname}`
  if (!e.origin || !e.origin.startsWith(allowedOrigin)) {
    return
  }

  if (e.data && e.data.messageType === 'aoepaymentcreated') {
    logger.debug('rmaPaymentMessageHandler', 'aoepaymentcreated', e.data.messageData)
    closeAllPaymentPanels()
    await refreshRmaData()
  }

  if (e.data && e.data.messageType === 'aoepaymentcreationerror') {
    logger.debug('rmaPaymentMessageHandler', 'aoepaymentcreationerror', e.data.messageData)
    closeAllPaymentPanels()

    const result = await AlertErrorResponse(t, e.data.messageData as IErrorResponse)
    if (result) {
      return
    }
    AlertUnknownError(t)
  }
}

// Address management functions
function editAddress() {
  showAddressSelection.value = true
  logger.debug('Opening address selection')
}

async function loadAddressList() {
  try {
    isLoadingAddresses.value = true
    const beeNumber = await GetBeeNumberAuthenicatedUser()
    if (!beeNumber) {
      logger.error('No authenticated user bee number found for address loading')
      return
    }

    const response = await PwsProfileAddressGet({ beeNumber })
    logger.debug('Address list response received:', response)

    if (response?.item?.allAddresses) {
      addressList.value = response.item.allAddresses.map(wrapper => ({
        ...wrapper.address,
        addressId: wrapper.addressId
      })) as IAddressWithId[]
    } else {
      addressList.value = []
    }
  } catch (error) {
    logger.error('Failed to load address list:', error)
    addressList.value = []
  } finally {
    isLoadingAddresses.value = false
  }
}

function selectExistingAddress(address: IAddressWithId) {
  selectedAddressId.value = address.addressId
  showAddressSelection.value = false
  rmaCreated.value = true
  logger.debug('Selected address:', address)
}

function cancelAddressSelection() {
  showAddressSelection.value = false
  // Don't clear selectedAddressId - keep the previously selected address
  logger.debug('Cancelled address selection')
}

async function addNewAddress() {
  currentAddressId.value = null
  const defaultCountry = await GetCountryCode()

  addressForm.value = {
    firstName: '',
    lastName: '',
    address1: '',
    address2: '',
    houseNumber: '',
    city: '',
    state: '',
    postalCode: '',
    country: defaultCountry
  }
  addressNameDisplay.value = ''
  showAddressForm.value = true
  showAddressSelection.value = false
}

function cancelAddressForm() {
  showAddressForm.value = false
  showAddressSelection.value = true
}

function onAddressChange(updatedAddress: {
  addressLine1: string
  addressLine2: string
  houseNumber: string
  city: string
  stateCode: string
  postalCode: string
  countryCode: string
  firstName: string
  lastName: string
}) {
  addressForm.value = {
    ...addressForm.value,
    firstName: updatedAddress.firstName,
    lastName: updatedAddress.lastName,
    address1: updatedAddress.addressLine1,
    address2: updatedAddress.addressLine2,
    houseNumber: updatedAddress.houseNumber,
    city: updatedAddress.city,
    state: updatedAddress.stateCode,
    postalCode: updatedAddress.postalCode,
    country: updatedAddress.countryCode
  }
}

async function saveAddress() {
  try {
    // Validate AddressControl fields first
    if (addressControlRef.value && !addressControlRef.value.validate()) {
      return
    }

    // Validate required fields
    if (!addressForm.value.firstName?.trim() || !addressForm.value.address1?.trim() ||
        !addressForm.value.city?.trim() || !addressForm.value.postalCode?.trim() ||
        !addressForm.value.country?.trim()) {
      await Swal.fire({
        title: t('labels.Error'),
        text: t('rma.RequiredAddressFields'),
        icon: 'error',
        confirmButtonText: t('buttons.Okay')
      })
      return
    }

    const beeNumber = await GetBeeNumberAuthenicatedUser()
    if (!beeNumber) {
      logger.error('No authenticated user bee number found for address save')
      return
    }

    const addressData = {
      addressId: currentAddressId.value || undefined,
      firstName: addressForm.value.firstName,
      lastName: addressForm.value.lastName,
      addressLine1: addressForm.value.address1,
      addressLine2: addressForm.value.address2 || undefined,
      houseNumber: addressForm.value.houseNumber || undefined,
      city: addressForm.value.city,
      stateCode: addressForm.value.state,
      postalCode: addressForm.value.postalCode,
      countryCode: addressForm.value.country
    }

    const response = await PwsProfileAddressSave({
      beeNumber,
      addressId: currentAddressId.value,
      address: addressData,
      isDefaultBilling: false,
      isDefaultShipping: false,
      isDefaultMailing: false,
      SkipAddressValidation: false
    })

    if (response?.addressValidationMessageId) {
      // Handle address verification
      if (response?.validAddresses && response.validAddresses.length > 0) {
        verifyAddress.value = response.validAddresses.map((addr: {
          addressLine1: string
          addressLine2: string
          city: string
          stateCode: string
          postalCode: string
          countryCode: string
          addressGoodDate?: string
        }, index: number) => ({
          addressId: index + 1,
          addressLine1: addr.addressLine1,
          addressLine2: addr.addressLine2 || '',
          houseNumber: null,
          city: addr.city,
          stateCode: addr.stateCode,
          postalCode: addr.postalCode,
          countryCode: addr.countryCode,
          addressGoodDate: addr.addressGoodDate || new Date().toISOString()
        }))
      } else {
        verifyAddress.value = []
      }

      addressVerifyError.value = true

      // Save current scroll position before opening popup
      savedScrollPosition.value = window.scrollY

      addressVerificationShow.value = true

      // Scroll to top to ensure popup is visible
      window.scrollTo({ top: 0, behavior: 'smooth' })

      return
    }

    if (response?.success || !response?.responseStatus?.errorCode) {
      await Swal.fire({
        title: t('rma.AddressSaved'),
        icon: 'success',
        confirmButtonText: t('buttons.Okay')
      })

      // Reload address list to get the newly created address with its ID
      await loadAddressList()

      // Find and select the newly created address by matching the form data
      const newAddress = addressList.value.find(addr =>
        addr.addressLine1 === addressData.addressLine1 &&
        addr.city === addressData.city &&
        addr.postalCode === addressData.postalCode &&
        addr.countryCode === addressData.countryCode
      )

      if (newAddress) {
        selectedAddressId.value = newAddress.addressId
        logger.debug('Auto-selected newly created address:', newAddress)
      }

      showAddressForm.value = false
      showAddressSelection.value = false
    } else {
      throw new Error(response?.message || t('rma.AddressSaveError'))
    }
  } catch (error) {
    logger.error('Address save failed:', error)
    await handleError(error, t, {
      title: t('labels.Error'),
      confirmButtonText: t('buttons.Okay'),
    })
  }
}

function useEnteredAddressClick() {
  addressVerificationShow.value = false

  // Restore scroll position
  window.scrollTo({ top: savedScrollPosition.value, behavior: 'smooth' })

  saveAddressWithSkipValidation()
}

function useSelectedAddressClick(selectedAddress: IAddress) {
  addressForm.value = {
    ...addressForm.value,
    firstName: selectedAddress.firstName || '',
    lastName: selectedAddress.lastName || '',
    address1: selectedAddress.addressLine1 || '',
    address2: selectedAddress.addressLine2 || '',
    houseNumber: selectedAddress.houseNumber || '',
    city: selectedAddress.city || '',
    state: selectedAddress.stateCode || '',
    postalCode: selectedAddress.postalCode || '',
    country: selectedAddress.countryCode || ''
  }
  addressNameDisplay.value = (selectedAddress.firstName || '') + ' ' + (selectedAddress.lastName || '')

  addressVerificationShow.value = false

  // Restore scroll position
  window.scrollTo({ top: savedScrollPosition.value, behavior: 'smooth' })

  saveAddressWithSkipValidation()
}

function reEnterAddressClick() {
  addressVerificationShow.value = false

  // Restore scroll position
  window.scrollTo({ top: savedScrollPosition.value, behavior: 'smooth' })
}

async function saveAddressWithSkipValidation() {
  try {
    const beeNumber = await GetBeeNumberAuthenicatedUser()
    if (!beeNumber) {
      logger.error('No authenticated user bee number found')
      return
    }

    const addressData = {
      addressId: currentAddressId.value || undefined,
      firstName: addressForm.value.firstName,
      lastName: addressForm.value.lastName,
      addressLine1: addressForm.value.address1,
      addressLine2: addressForm.value.address2 || undefined,
      houseNumber: addressForm.value.houseNumber || undefined,
      city: addressForm.value.city,
      stateCode: addressForm.value.state,
      postalCode: addressForm.value.postalCode,
      countryCode: addressForm.value.country
    }

    const response = await PwsProfileAddressSave({
      beeNumber,
      addressId: currentAddressId.value,
      address: addressData,
      isDefaultBilling: false,
      isDefaultShipping: false,
      isDefaultMailing: false,
      SkipAddressValidation: true
    })

    if (response?.success || !response?.responseStatus?.errorCode) {
      await Swal.fire({
        title: t('rma.AddressSaved'),
        icon: 'success',
        confirmButtonText: t('buttons.Okay')
      })

      // Reload address list to get the newly created address with its ID
      await loadAddressList()

      // Find and select the newly created address by matching the form data
      const newAddress = addressList.value.find(addr =>
        addr.addressLine1 === addressData.addressLine1 &&
        addr.city === addressData.city &&
        addr.postalCode === addressData.postalCode &&
        addr.countryCode === addressData.countryCode
      )

      if (newAddress) {
        selectedAddressId.value = newAddress.addressId
        logger.debug('Auto-selected newly created address:', newAddress)
      }

      showAddressForm.value = false
      showAddressSelection.value = false
    }
  } catch (error) {
    logger.error('Address save failed:', error)
  }
}

async function submitRMA() {
  if (!orderDetails.value) {
    errorMessage.value = t('rma.errors.orderDetailsNotFound')
    return
  }

  if (!createdRmaNumber.value) {
    errorMessage.value = t('rma.errors.rmaNotCreated')
    return
  }

  // Prevent duplicate submissions
  if (isSubmittingRMA.value) {
    logger.debug('RMA submission already in progress, ignoring duplicate click')
    return
  }

  try {
    isSubmittingRMA.value = true
    SetIsLoading(true)
    errorMessage.value = ''

    const rmaNumber = createdRmaNumber.value

    logger.debug('Submitting RMA:', rmaNumber)
    await RmaSubmit(rmaNumber)
    logger.debug('RMA submitted successfully')

    // Scroll to top of page for SweetAlert visibility
    window.scrollTo({ top: 0, behavior: 'smooth' })

    // Show success message with SweetAlert
    await Swal.fire({
      icon: 'success',
      title: t('rma.SubmitSuccess'),
      text: t('rma.RMASubmittedSuccessfully', { rmaNumber }),
      confirmButtonColor: 'var(--vt-c-teal)'
    })

    // Reset the form after successful submission to prevent duplicate submissions
    resetForm()

  } catch (error) {
    logger.error('Error submitting RMA:', error)
    errorMessage.value = t('rma.errors.failedToSubmitRMA')
    // Re-enable button on error so user can retry
    isSubmittingRMA.value = false
  } finally {
    SetIsLoading(false)
  }
}

async function proceedWithReturn() {
  if (selectedItems.value.size === 0 && selectedComponents.value.size === 0) {
    errorMessage.value = t('rma.errors.selectAtLeastOneItemToReturn')
    return
  }

  // Check that all selected items have a reason
  const selectedItemsArray = Array.from(selectedItems.value)
  const missingReasons = selectedItemsArray.filter(lineNumber => !itemReasons.value.has(lineNumber))

  if (missingReasons.length > 0) {
    errorMessage.value = t('rma.errors.selectReasonForAllSelectedItems')
    return
  }

  // Check that all selected components have a reason
  const selectedComponentsArray = Array.from(selectedComponents.value)
  const missingComponentReasons = selectedComponentsArray.filter(componentKey => !componentReasons.value.has(componentKey))

  if (missingComponentReasons.length > 0) {
    errorMessage.value = t('rma.errors.selectReasonForAllSelectedComponents')
    return
  }

  // Check that items with exchange reason have an exchange item selected
  const exchangeItemsMissingSelection = selectedItemsArray.filter(lineNumber => {
    const reason = itemReasons.value.get(lineNumber)
    const isExchange = reason?.code === 'EXR'
    const hasExchangeItem = exchangeItems.value.has(lineNumber)

    logger.debug('Checking exchange item for line:', lineNumber, {
      reason: reason?.code,
      isExchange,
      hasExchangeItem,
      exchangeItemsMap: Array.from(exchangeItems.value.entries())
    })

    return isExchange && !hasExchangeItem
  })

  logger.debug('Exchange items missing selection:', exchangeItemsMissingSelection)

  if (exchangeItemsMissingSelection.length > 0) {
    // Scroll to top for error visibility
    window.scrollTo({ top: 0, behavior: 'smooth' })

    await Swal.fire({
      icon: 'warning',
      title: t('rma.ExchangeItemRequired'),
      text: t('rma.ExchangeItemRequiredMessage'),
      confirmButtonColor: 'var(--vt-c-teal)'
    })
    return
  }

  // Check that components with exchange reason have an exchange item selected
  const componentExchangeItemsMissingSelection = selectedComponentsArray.filter(componentKey => {
    const reason = componentReasons.value.get(componentKey)
    const isExchange = reason?.code === 'EXR'
    const hasExchangeItem = componentExchangeItems.value.has(componentKey)

    logger.debug('Checking component exchange item for:', componentKey, {
      reason: reason?.code,
      isExchange,
      hasExchangeItem
    })

    return isExchange && !hasExchangeItem
  })

  if (componentExchangeItemsMissingSelection.length > 0) {
    window.scrollTo({ top: 0, behavior: 'smooth' })

    await Swal.fire({
      icon: 'warning',
      title: t('rma.ExchangeItemRequired'),
      text: t('rma.ExchangeItemRequiredMessage'),
      confirmButtonColor: 'var(--vt-c-teal)'
    })
    return
  }

  // Check that items with required photos have photos uploaded
  const itemsMissingRequiredPhotos = selectedItemsArray.filter(lineNumber => {
    const isRequired = isPhotoRequired(lineNumber)
    const photos = itemPhotos.value.get(lineNumber)
    const hasPhotos = photos && photos.length > 0

    logger.debug('Checking required photos for line:', lineNumber, {
      isRequired,
      hasPhotos,
      photoCount: photos?.length || 0
    })

    return isRequired && !hasPhotos
  })

  logger.debug('Items missing required photos:', itemsMissingRequiredPhotos)

  if (itemsMissingRequiredPhotos.length > 0) {
    // Scroll to top for error visibility
    window.scrollTo({ top: 0, behavior: 'smooth' })

    await Swal.fire({
      icon: 'warning',
      title: t('rma.PhotoRequired'),
      text: t('rma.PhotoRequiredMessage'),
      confirmButtonColor: 'var(--vt-c-teal)'
    })
    return
  }

  // Check that components with required photos have photos uploaded
  const componentsMissingRequiredPhotos = selectedComponentsArray.filter(componentKey => {
    const [, componentLineNumber] = componentKey.split('-').map(Number)
    const isRequired = isPhotoRequired(componentLineNumber)
    const photos = componentPhotos.value.get(componentKey)
    const hasPhotos = photos && photos.length > 0

    logger.debug('Checking required photos for component:', componentKey, {
      isRequired,
      hasPhotos,
      photoCount: photos?.length || 0
    })

    return isRequired && !hasPhotos
  })

  if (componentsMissingRequiredPhotos.length > 0) {
    window.scrollTo({ top: 0, behavior: 'smooth' })

    await Swal.fire({
      icon: 'warning',
      title: t('rma.PhotoRequired'),
      text: t('rma.PhotoRequiredMessage'),
      confirmButtonColor: 'var(--vt-c-teal)'
    })
    return
  }

  if (!orderDetails.value) {
    errorMessage.value = t('rma.errors.orderDetailsNotFound')
    return
  }

  // Store the items to receive for the next section (parent items)
  itemsToReceive.value = selectedItemsArray.map(lineNumber => {
    const item = orderDetails.value!.item.skus.find(sku => sku.lineNumber === lineNumber)!
    const reason = itemReasons.value.get(lineNumber)!
    const quantity = getItemQuantity(lineNumber, getRemainingQuantity(lineNumber, item.quantity))
    return { item, reason, quantity }
  })

  // Add selected components to items to receive
  selectedComponentsArray.forEach(componentKey => {
    const [parentLineNumber, componentLineNumber] = componentKey.split('-').map(Number)
    const parentItem = orderDetails.value!.item.skus.find(sku => sku.lineNumber === parentLineNumber)
    const component = parentItem?.components?.find(c => c.componentLineNumber === componentLineNumber)

    if (component) {
      const reason = componentReasons.value.get(componentKey)!
      const quantity = getComponentQuantity(parentLineNumber, componentLineNumber, getRemainingComponentQuantity(parentLineNumber, componentLineNumber, component.quantity))

      // Create a pseudo-item from the component for display
      const componentAsItem: IOrderDetailGetItemSku = {
        lineNumber: componentLineNumber,
        skuCode: component.skuCode,
        productCode: component.productCode,
        productName: component.productName,
        price: component.priceExtended / component.quantity, // Get unit price
        quantity: component.quantity,
        priceExtended: component.priceExtended,
        imageUrl: '', // Components don't have images, use empty string
        components: []
      }

      itemsToReceive.value.push({ item: componentAsItem, reason, quantity })
    }
  })

  try {
    SetIsLoading(true)
    errorMessage.value = ''

    // Step 1: Reuse the RMA from a previous pass if the user went back and changed their
    // selection (its lines were already cleared in goBackToItems), otherwise create a new one
    let rmaNumber = createdRmaNumber.value

    if (rmaNumber) {
      logger.debug('Reusing existing RMA after going back:', rmaNumber)
    } else {
      logger.debug('Creating RMA for order:', orderDetails.value.item.aoeOrderId)

      const rmaResponse = await RmaCreate(
        orderDetails.value.item.aoeOrderId,
        selectedAddressId.value || undefined
      )
      logger.debug('RMA created:', rmaResponse)

      if (!rmaResponse || !rmaResponse.rmaNumber) {
        errorMessage.value = t('rma.errors.failedToCreateRMA')
        return
      }

      rmaNumber = rmaResponse.rmaNumber
      createdRmaNumber.value = rmaNumber
      logger.debug('RMA Number:', rmaNumber)
    }

    // Step 2: Create inbound SKU entries for each selected item
    const inboundPromises = itemsToReceive.value.map(async (receiveItem) => {
      logger.debug('Creating inbound SKU:', {
        rmaNumber,
        skuCode: receiveItem.item.skuCode,
        quantity: receiveItem.quantity,
        orderLineNumber: receiveItem.item.lineNumber,
        reasonSystemKeyword: receiveItem.reason.code
      })

      return RmaInboundSkuCreate(
        rmaNumber,
        receiveItem.item.skuCode,
        receiveItem.quantity,
        receiveItem.item.lineNumber,
        receiveItem.reason.code
      )
    })

    await Promise.all(inboundPromises)
    logger.debug('All inbound SKUs created successfully')

    // Step 2b: Create inbound SKU entries for each selected component
    const componentInboundPromises = selectedComponentsArray.map(async (componentKey) => {
      const [parentLineNumber, componentLineNumber] = componentKey.split('-').map(Number)
      const parentItem = orderDetails.value!.item.skus.find(sku => sku.lineNumber === parentLineNumber)
      const component = parentItem?.components?.find(c => c.componentLineNumber === componentLineNumber)

      if (!component) {
        logger.error('Component not found:', componentKey)
        return
      }

      const reason = componentReasons.value.get(componentKey)!
      const quantity = getComponentQuantity(parentLineNumber, componentLineNumber, getRemainingComponentQuantity(parentLineNumber, componentLineNumber, component.quantity))

      logger.debug('Creating inbound SKU for component:', {
        rmaNumber,
        skuCode: component.skuCode,
        quantity,
        orderLineNumber: componentLineNumber,
        reasonSystemKeyword: reason.code
      })

      return RmaInboundSkuCreate(
        rmaNumber,
        component.skuCode,
        quantity,
        componentLineNumber,
        reason.code
      )
    })

    await Promise.all(componentInboundPromises)
    logger.debug('All component inbound SKUs created successfully')

    // Step 2.5: Upload and delete photos for each inbound SKU (items and components)
    const photoPromises: Promise<unknown>[] = []

    itemsToReceive.value.forEach((receiveItem) => {
      const lineNumber = receiveItem.item.lineNumber
      const files = itemPhotos.value.get(lineNumber)
      const deletedFiles = itemDeletedPhotos.value.get(lineNumber)

      // Upload new photos
      if (files && files.length > 0) {
        files.forEach((file) => {
          if (file && file.getRawFile) {
            logger.debug('Uploading photo for line:', lineNumber, file.name)
            const uploadPromise = RmaInboundPhotoCreate(
              {
                rmaNumber,
                lineNumber,
                fileName: file.name,
              },
              file.getRawFile()
            )
              .then((response) => {
                logger.debug('Photo uploaded successfully:', response)
              })
              .catch((error) => {
                logger.error('Error uploading photo:', error)
              })
            photoPromises.push(uploadPromise)
          }
        })
      }

      // Delete removed photos
      if (deletedFiles && deletedFiles.length > 0) {
        deletedFiles.forEach((file) => {
          logger.debug('Deleting photo for line:', lineNumber, file.uid)
          const deletePromise = RmaInboundPhotoDelete(rmaNumber, lineNumber, file.uid)
            .then((response) => {
              logger.debug('Photo deleted successfully:', response)
            })
            .catch((error) => {
              logger.error('Error deleting photo:', error)
            })
          photoPromises.push(deletePromise)
        })
      }
    })

    // Handle component photos
    selectedComponentsArray.forEach((componentKey) => {
      const [, componentLineNumber] = componentKey.split('-').map(Number)
      const files = componentPhotos.value.get(componentKey)
      const deletedFiles = componentDeletedPhotos.value.get(componentKey)

      // Upload new photos for components
      if (files && files.length > 0) {
        files.forEach((file) => {
          if (file && file.getRawFile) {
            logger.debug('Uploading photo for component:', componentKey, file.name)
            const uploadPromise = RmaInboundPhotoCreate(
              {
                rmaNumber,
                lineNumber: componentLineNumber,
                fileName: file.name,
              },
              file.getRawFile()
            )
              .then((response) => {
                logger.debug('Component photo uploaded successfully:', response)
              })
              .catch((error) => {
                logger.error('Error uploading component photo:', error)
              })
            photoPromises.push(uploadPromise)
          }
        })
      }

      // Delete removed photos for components
      if (deletedFiles && deletedFiles.length > 0) {
        deletedFiles.forEach((file) => {
          logger.debug('Deleting photo for component:', componentKey, file.uid)
          const deletePromise = RmaInboundPhotoDelete(rmaNumber, componentLineNumber, file.uid)
            .then((response) => {
              logger.debug('Component photo deleted successfully:', response)
            })
            .catch((error) => {
              logger.error('Error deleting component photo:', error)
            })
          photoPromises.push(deletePromise)
        })
      }
    })

    if (photoPromises.length > 0) {
      await Promise.all(photoPromises)
      logger.debug('All photo operations completed')
    }

    // Step 3: Create outbound SKU entries only for items with exchange reason and exchange SKU selected
    const itemsWithExchanges = itemsToReceive.value.filter(receiveItem => {
      const isExchange = receiveItem.reason.code === 'EXR'
      const hasExchangeItem = exchangeItems.value.has(receiveItem.item.lineNumber)
      return isExchange && hasExchangeItem
    })

    if (itemsWithExchanges.length > 0) {
      const outboundPromises = itemsWithExchanges.map(async (receiveItem) => {
        const exchangeItem = exchangeItems.value.get(receiveItem.item.lineNumber)
        if (!exchangeItem) return

        logger.debug('Creating outbound SKU for exchange:', {
          rmaNumber,
          skuCode: exchangeItem.skuCode,
          quantity: receiveItem.quantity
        })

        return RmaOutboundSkuCreate(
          rmaNumber,
          exchangeItem.skuCode,
          receiveItem.quantity
        )
      })

      await Promise.all(outboundPromises)
      logger.debug('All outbound SKUs created successfully for exchanges')
    } else {
      logger.debug('No exchange items to create outbound SKUs for')
    }

    // Step 3b: Create outbound SKU entries for components with exchange reason
    const componentsWithExchanges = selectedComponentsArray.filter(componentKey => {
      const reason = componentReasons.value.get(componentKey)
      const isExchange = reason?.code === 'EXR'
      const hasExchangeItem = componentExchangeItems.value.has(componentKey)
      return isExchange && hasExchangeItem
    })

    if (componentsWithExchanges.length > 0) {
      const componentOutboundPromises = componentsWithExchanges.map(async (componentKey) => {
        const [parentLineNumber, componentLineNumber] = componentKey.split('-').map(Number)
        const parentItem = orderDetails.value!.item.skus.find(sku => sku.lineNumber === parentLineNumber)
        const component = parentItem?.components?.find(c => c.componentLineNumber === componentLineNumber)
        const exchangeItem = componentExchangeItems.value.get(componentKey)

        if (!component || !exchangeItem) return

        const quantity = getComponentQuantity(parentLineNumber, componentLineNumber, getRemainingComponentQuantity(parentLineNumber, componentLineNumber, component.quantity))

        logger.debug('Creating outbound SKU for component exchange:', {
          rmaNumber,
          skuCode: exchangeItem.skuCode,
          quantity
        })

        return RmaOutboundSkuCreate(
          rmaNumber,
          exchangeItem.skuCode,
          quantity
        )
      })

      await Promise.all(componentOutboundPromises)
      logger.debug('All component outbound SKUs created successfully for exchanges')
    } else {
      logger.debug('No component exchange items to create outbound SKUs for')
    }

    // Step 4: Get the RMA to check if payment is needed
    logger.debug('Fetching RMA details to check payment requirements')
    const rmaGetResponse = await RmaGet(rmaNumber)
    logger.debug('RMA details retrieved:', rmaGetResponse)
    rmaData.value = rmaGetResponse
    refreshOriginalPaymentMethods()
    await loadAvailablePaymentMethodsForCharge()

    // Load addresses and prepare for address selection
    await continueToAddress()

    logger.debug('RMA created and items prepared for submission')
  } catch (error) {
    logger.error('Error creating RMA:', error)
    errorMessage.value = t('rma.errors.failedToCreateRMA')
  } finally {
    SetIsLoading(false)
  }
}
</script>

<template>
  <div class="aoe-rma-entry">
    <div class="exit-button-container">
      <KButton
        :theme-color="'secondary'"
        @click="goBack"
        class="exit-button"
      >
        <span class="exit-icon">←</span> {{ t('rma.Exit') }}
      </KButton>
    </div>
    <div class="rma-header">
      <h1>{{ t('rma.InitiateReturn') }}</h1>
      <p>{{ t('rma.EnterOrderNumber') }}</p>
    </div>

    <div class="order-search-section">
      <div class="search-container">
        <div class="search-input-group">
          <label for="orderNumber">{{ t('rma.OrderNumber') }}</label>
          <KInput
            id="orderNumber"
            v-model="orderNumber"
            :placeholder="t('rma.OrderNumberPlaceholder')"
            @keyup.enter="searchOrder"
            class="order-input"
          />
        </div>
        <KButton
          :theme-color="'primary'"
          @click="searchOrder"
          :disabled="isSearching || !orderNumber.trim()"
          class="search-buttonx"
        >
          {{ isSearching ? t('rma.Searching') : t('rma.Search') }}
        </KButton>
      </div>

      <div v-if="errorMessage" class="alert alert-danger">
        {{ errorMessage }}
      </div>
    </div>

    <!-- Existing RMA Warning Section -->
    <div v-if="hasExistingRma" class="existing-rma-section">
      <div class="existing-rma-card">
        <div class="existing-rma-icon">⚠️</div>
        <h2>{{ t('rma.ExistingRMAFound') }}</h2>
        <p class="existing-rma-message">
          {{ t('rma.ExistingRMAMessage') }}
        </p>
        <div class="existing-rma-number">
          <strong>{{ t('rma.RMANumber') }}:</strong> {{ existingRmaNumber }}
        </div>
        <p class="existing-rma-info">
          {{ t('rma.ExistingRMAInfo') }}
        </p>
      </div>
    </div>

    <div v-if="orderDetails && !hasExistingRma" class="order-details-section">
      <!-- New 4-column order details layout -->
      <div class="order-details-grid">
        <!-- Details Column -->
        <div class="details-column">
          <h3>{{ t('rma.Details') }}</h3>
          <div class="detail-item">
            <span class="detail-label">{{ t('rma.OrderType') }}:</span>
            <span class="detail-value">{{ orderDetails.item.orderTypeId || t('rma.NotAvailable') }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{ t('rma.OrderTotal', 'Order Total') }}:</span>
            <span class="detail-value">{{ FormatCurrency(orderDetails.item.total ?? 0, null) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{ t('rma.LastUpdated', 'Last updated') }}:</span>
            <span class="detail-value">{{ formatDate(orderDetails.item.orderTime || orderListItem?.orderTime) || t('rma.NotAvailable') }}</span>
          </div>
        </div>

        <!-- Orders Column -->
        <div class="orders-column">
          <h3>{{ t('rma.Orders') }}</h3>
          <div class="detail-item">
            <span class="detail-label">{{ t('rma.OrderNumber') }}:</span>
            <span class="detail-value">{{ orderDetails.item.aoeOrderId }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{ t('rma.OrderDate', 'Order date') }}:</span>
            <span class="detail-value">{{ formatDate(orderDetails.item.orderTime || orderListItem?.orderTime) || t('rma.NotAvailable') }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{ t('rma.ComplaintSubmitted') }}:</span>
            <span class="detail-value">{{ getRmaCount() }}</span>
          </div>
        </div>

        <!-- Guests Column -->
        <div class="guests-column">
          <h3>{{ t('rma.Guests') }}</h3>
          <div class="detail-item">
            <span class="detail-label">{{ t('rma.Customer') }}:</span>
            <span class="detail-value">{{ getCustomerName() }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{ t('rma.DeliveryAddress') }}:</span>
            <span class="detail-value">{{ formatShippingAddress() }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{ t('rma.EmailAddress') }}:</span>
            <span class="detail-value">{{ getCustomerEmail() }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">{{ t('rma.PhoneNumber') }}:</span>
            <span class="detail-value">{{ getCustomerPhone() }}</span>
          </div>
        </div>

        <!-- Consultant Column -->
        <div class="consultant-column">
          <h3>{{ t('rma.Consultant') }}</h3>
          <div class="detail-item">
            <span class="detail-label">{{ t('rma.Advisor') }}:</span>
            <span class="detail-value">{{ getConsultantName() }}</span>
          </div>
        </div>
      </div>

      <!-- Reason Codes Explanation Section -->
      <div class="reason-codes-section">
        <h3>{{ t('rma.ReasonCodesTitle') }}</h3>
        <div class="reason-code-list">
          <div class="reason-code-item">
            <strong>{{ t('rma.ReasonCodeDUR') }}:</strong>
            <span>{{ t('rma.ReasonCodeDURDescription') }}</span>
          </div>
          <div class="reason-code-item">
            <strong>{{ t('rma.ReasonCodeDSR') }}:</strong>
            <span>{{ t('rma.ReasonCodeDSRDescription') }}</span>
          </div>
          <div class="reason-code-item">
            <strong>{{ t('rma.ReasonCodeEX') }}:</strong>
            <span>{{ t('rma.ReasonCodeEXDescription') }}</span>
          </div>
        </div>
        <div class="reason-codes-note">
          <p>{{ t('rma.ReasonCodesNote') }}</p>
        </div>
      </div>

      <div class="items-selection-section">
        <h3>{{ t('rma.SelectItemsToReturn') }}</h3>
        <p class="instruction-text">
          {{ t('rma.SelectItemsInstruction') }}
        </p>

        <div class="items-list">
          <div
            v-for="item in orderDetails.item.skus"
            :key="item.lineNumber"
            class="item-wrapper"
          >
            <div
              class="item-card"
              :class="{ 'item-selected': isItemSelected(item.lineNumber) }"
            >
              <div class="item-checkbox">
              <input
                type="checkbox"
                class="k-checkbox"
                :id="`item-${item.lineNumber}`"
                :checked="isItemSelected(item.lineNumber)"
                @change="toggleItemSelection(item.lineNumber)"
                :disabled="rmaCreated || isItemDisabled(item.lineNumber)"
              />
            </div>
            <div class="item-details">
              <div class="item-header">
                <h4 class="item-name">{{ item.productName }}</h4>
                <div class="item-price">{{ FormatCurrency(item.price ?? 0, null) }}</div>
              </div>
              <div class="item-info">
                <div class="info-row">
                  <span class="rma-label">{{ t('rma.SKU') }}:</span>
                  <span class="value">{{ item.skuCode }}</span>
                </div>
                <div class="info-row">
                  <span class="rma-label">{{ t('rma.ProductCode') }}:</span>
                  <span class="value">{{ item.productCode }}</span>
                </div>
                <div class="info-row quantity-row">
                  <span class="rma-label">{{ t('rma.Quantity') }}:</span>
                  <DropDownList
                    :model-value="getItemQuantity(item.lineNumber, getRemainingQuantity(item.lineNumber, item.quantity))"
                    @update:model-value="setItemQuantity(item.lineNumber, $event)"
                    :data-items="getQuantityOptions(getRemainingQuantity(item.lineNumber, item.quantity))"
                    :disabled="rmaCreated || hasAnyComponentSelected(item.lineNumber)"
                    class="quantity-dropdown"
                  />
                </div>
                <div class="info-row">
                  <span class="rma-label">{{ t('rma.LineTotal', 'Line Total') }}:</span>
                  <span class="value">{{ FormatCurrency((item.price ?? 0) * (item.quantity ?? 0), null) }}</span>
                </div>
              </div>

              <div v-if="isItemSelected(item.lineNumber)" class="item-reason-section">
                <label :for="`reason-${item.lineNumber}`" class="reason-label">
                  {{ t('rma.ReturnReason') }}:
                </label>
                <DropDownList
                  :id="`reason-${item.lineNumber}`"
                  :model-value="getItemReason(item.lineNumber)"
                  @update:model-value="setItemReason(item.lineNumber, $event)"
                  :data-items="returnReasons"
                  :text-field="'label'"
                  :data-item-key="'code'"
                  :default-item="{ code: '', label: t('rma.SelectReasonPlaceholder') }"
                  :disabled="rmaCreated"
                  class="item-reason-dropdown"
                />

                <!-- Exchange Item Selection -->
                <div v-if="isExchangeReason(item.lineNumber)" class="exchange-item-section">
                  <div v-if="!getExchangeItem(item.lineNumber)" class="exchange-search">
                    <label :for="`exchange-search-${item.lineNumber}`" class="exchange-label">
                      {{ t('rma.SelectExchangeItem') }}:
                    </label>
                    <AutoComplete
                      :id="`exchange-search-${item.lineNumber}`"
                      :value="getExchangeSearchText(item.lineNumber)"
                      @change="handleExchangeSearch(item.lineNumber, $event)"
                      :data-items="getExchangeSearchResults(item.lineNumber)"
                      :text-field="'productAndSku'"
                      :placeholder="t('rma.SearchForProduct')"
                      :disabled="rmaCreated"
                      class="exchange-autocomplete"
                    >
                      <template v-slot:item="{ props: itemProps }">
                        <li class="k-list-item" @click="selectExchangeItem(item.lineNumber, itemProps.dataItem)">
                          <div class="exchange-search-item">
                            <img
                              v-if="itemProps.dataItem.fullProductImageUrl"
                              :src="itemProps.dataItem.fullProductImageUrl"
                              :alt="itemProps.dataItem.productName"
                              class="exchange-item-image"
                              @error="(e) => (e.target as HTMLImageElement).style.display = 'none'"
                            />
                            <div class="exchange-item-info">
                              <div class="exchange-item-name">{{ itemProps.dataItem.productName }}</div>
                              <div class="exchange-item-sku">{{ t('labels.SkuPrefix', { sku: itemProps.dataItem.skuCode }) }}</div>
                            </div>
                          </div>
                        </li>
                      </template>
                    </AutoComplete>
                  </div>

                  <!-- Selected Exchange Item Display -->
                  <div v-else class="exchange-item-selected">
                    <div class="exchange-selected-header">
                      <strong>{{ t('rma.ExchangeItemSelected') }}:</strong>
                      <KButton
                        :theme-color="'error'"
                        :fill-mode="'flat'"
                        @click="removeExchangeItem(item.lineNumber)"
                        :disabled="rmaCreated"
                        class="remove-exchange-button"
                      >
                        {{ t('rma.RemoveExchangeItem') }}
                      </KButton>
                    </div>
                    <div class="exchange-selected-content">
                      <img
                        v-if="getExchangeItem(item.lineNumber)?.imageUrl"
                        :src="getExchangeItem(item.lineNumber)!.imageUrl"
                        :alt="getExchangeItem(item.lineNumber)!.productName"
                        class="exchange-selected-image"
                      />
                      <div class="exchange-selected-info">
                        <div class="exchange-selected-name">{{ getExchangeItem(item.lineNumber)!.productName }}</div>
                        <div class="exchange-selected-sku">{{ t('labels.SkuPrefix', { sku: getExchangeItem(item.lineNumber)!.skuCode }) }}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Photo Uploader - Only show if photo eligible -->
                <div v-if="isPhotoEligible(item.lineNumber)" class="photo-uploader-section">
                  <label class="photo-label">
                    {{ t('rma.UploadPhotos') }}
                    <span v-if="isPhotoRequired(item.lineNumber)" class="required-indicator">*</span>
                    <span v-if="isPhotoRequired(item.lineNumber)" class="required-text">
                      ({{ t('rma.PhotoRequired') }})
                    </span>
                  </label>
                  <RMAPhotoUploader
                    :disabled="rmaCreated || !isItemSelected(item.lineNumber)"
                    :files="getItemPhotos(item.lineNumber)"
                    :existingFiles="[]"
                    :deletedFiles="getItemDeletedPhotos(item.lineNumber)"
                    @update:files="setItemPhotos(item.lineNumber, $event)"
                    @update:deletedFiles="setItemDeletedPhotos(item.lineNumber, $event)"
                  />
                </div>
              </div>
            </div>
            </div>

            <!-- Component SKUs - Display as full rows with all functionality -->
            <div v-if="hasComponents(item.lineNumber)" class="components-wrapper">
              <div class="components-header">
                <span class="components-label">{{ t('rma.SKUComponents') }}</span>
              </div>
              <div class="components-section">
              <div
                v-for="component in item.components"
                :key="`${item.lineNumber}-${component.componentLineNumber}`"
                class="component-card"
                :class="{ 'component-selected': isComponentSelected(item.lineNumber, component.componentLineNumber) }"
              >
                <div class="component-checkbox">
                  <input
                    type="checkbox"
                    class="k-checkbox"
                    :id="`component-${item.lineNumber}-${component.componentLineNumber}`"
                    :checked="isComponentSelected(item.lineNumber, component.componentLineNumber)"
                    @change="toggleComponentSelection(item.lineNumber, component.componentLineNumber)"
                    :disabled="rmaCreated || isComponentDisabled(item.lineNumber, component.componentLineNumber)"
                  />
                </div>
                <div class="component-details">
                  <div class="component-header">
                    <h5 class="component-name">{{ component.productName }}</h5>
                    <div class="component-price">{{ FormatCurrency(component.priceExtended ?? 0, null) }}</div>
                  </div>
                  <div class="component-info">
                    <div class="info-row">
                      <span class="rma-label">{{ t('rma.SKU') }}:</span>
                      <span class="value">{{ component.skuCode }}</span>
                    </div>
                    <div class="info-row">
                      <span class="rma-label">{{ t('rma.ProductCode') }}:</span>
                      <span class="value">{{ component.productCode }}</span>
                    </div>
                    <div class="info-row quantity-row">
                      <span class="rma-label">{{ t('rma.Quantity') }}:</span>
                      <DropDownList
                        :model-value="getComponentQuantity(item.lineNumber, component.componentLineNumber, getRemainingComponentQuantity(item.lineNumber, component.componentLineNumber, component.quantity))"
                        @update:model-value="setComponentQuantity(item.lineNumber, component.componentLineNumber, $event)"
                        :data-items="getQuantityOptions(getRemainingComponentQuantity(item.lineNumber, component.componentLineNumber, component.quantity))"
                        :disabled="rmaCreated || isItemSelected(item.lineNumber)"
                        class="quantity-dropdown"
                      />
                    </div>
                    <div class="info-row">
                      <span class="rma-label">{{ t('rma.LineTotal', 'Line Total') }}:</span>
                      <span class="value">{{ FormatCurrency(component.priceExtended ?? 0, null) }}</span>
                    </div>
                  </div>

                  <div v-if="isComponentSelected(item.lineNumber, component.componentLineNumber)" class="component-reason-section">
                    <label :for="`component-reason-${item.lineNumber}-${component.componentLineNumber}`" class="reason-label">
                      {{ t('rma.ReturnReason') }}:
                    </label>
                    <DropDownList
                      :id="`component-reason-${item.lineNumber}-${component.componentLineNumber}`"
                      :model-value="getComponentReason(item.lineNumber, component.componentLineNumber)"
                      @update:model-value="setComponentReason(item.lineNumber, component.componentLineNumber, $event)"
                      :data-items="returnReasons"
                      :text-field="'label'"
                      :data-item-key="'code'"
                      :default-item="{ code: '', label: t('rma.SelectReasonPlaceholder') }"
                      :disabled="rmaCreated"
                      class="component-reason-dropdown"
                    />

                    <!-- Component Exchange Item Selection -->
                    <div v-if="isComponentExchangeReason(item.lineNumber, component.componentLineNumber)" class="exchange-item-section">
                      <div v-if="!getComponentExchangeItem(item.lineNumber, component.componentLineNumber)" class="exchange-search">
                        <label :for="`component-exchange-search-${item.lineNumber}-${component.componentLineNumber}`" class="exchange-label">
                          {{ t('rma.SelectExchangeItem') }}:
                        </label>
                        <AutoComplete
                          :id="`component-exchange-search-${item.lineNumber}-${component.componentLineNumber}`"
                          :value="getComponentExchangeSearchText(item.lineNumber, component.componentLineNumber)"
                          @change="handleComponentExchangeSearch(item.lineNumber, component.componentLineNumber, $event)"
                          :data-items="getComponentExchangeSearchResults(item.lineNumber, component.componentLineNumber)"
                          :text-field="'productAndSku'"
                          :placeholder="t('rma.SearchForProduct')"
                          :disabled="rmaCreated"
                          class="exchange-autocomplete"
                        >
                          <template v-slot:item="{ props: itemProps }">
                            <li class="k-list-item" @click="selectComponentExchangeItem(item.lineNumber, component.componentLineNumber, itemProps.dataItem)">
                              <div class="exchange-search-item">
                                <img
                                  v-if="itemProps.dataItem.fullProductImageUrl"
                                  :src="itemProps.dataItem.fullProductImageUrl"
                                  :alt="itemProps.dataItem.productName"
                                  class="exchange-item-image"
                                  @error="(e) => (e.target as HTMLImageElement).style.display = 'none'"
                                />
                                <div class="exchange-item-info">
                                  <div class="exchange-item-name">{{ itemProps.dataItem.productName }}</div>
                                  <div class="exchange-item-sku">{{ t('rma.SKU') }}: {{ itemProps.dataItem.skuCode }}</div>
                                </div>
                              </div>
                            </li>
                          </template>
                        </AutoComplete>
                      </div>

                      <!-- Selected Component Exchange Item Display -->
                      <div v-else class="exchange-item-selected">
                        <div class="exchange-selected-header">
                          <strong>{{ t('rma.ExchangeItemSelected') }}:</strong>
                          <KButton
                            :theme-color="'error'"
                            :fill-mode="'flat'"
                            @click="removeComponentExchangeItem(item.lineNumber, component.componentLineNumber)"
                            :disabled="rmaCreated"
                            class="remove-exchange-button"
                          >
                            {{ t('rma.RemoveExchangeItem') }}
                          </KButton>
                        </div>
                        <div class="exchange-selected-content">
                          <img
                            v-if="getComponentExchangeItem(item.lineNumber, component.componentLineNumber)?.imageUrl"
                            :src="getComponentExchangeItem(item.lineNumber, component.componentLineNumber)!.imageUrl"
                            :alt="getComponentExchangeItem(item.lineNumber, component.componentLineNumber)!.productName"
                            class="exchange-selected-image"
                          />
                          <div class="exchange-selected-info">
                            <div class="exchange-selected-name">{{ getComponentExchangeItem(item.lineNumber, component.componentLineNumber)!.productName }}</div>
                            <div class="exchange-selected-sku">{{ t('rma.SKU') }}: {{ getComponentExchangeItem(item.lineNumber, component.componentLineNumber)!.skuCode }}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Component Photo Uploader - Only show if photo eligible -->
                    <div v-if="isPhotoEligible(component.componentLineNumber)" class="photo-uploader-section">
                      <label class="photo-label">
                        {{ t('rma.UploadPhotos') }}
                        <span v-if="isPhotoRequired(component.componentLineNumber)" class="required-indicator">*</span>
                        <span v-if="isPhotoRequired(component.componentLineNumber)" class="required-text">
                          ({{ t('rma.PhotoRequired') }})
                        </span>
                      </label>
                      <RMAPhotoUploader
                        :disabled="rmaCreated || !isComponentSelected(item.lineNumber, component.componentLineNumber)"
                        :files="getComponentPhotos(item.lineNumber, component.componentLineNumber)"
                        :existingFiles="[]"
                        :deletedFiles="getComponentDeletedPhotos(item.lineNumber, component.componentLineNumber)"
                        @update:files="setComponentPhotos(item.lineNumber, component.componentLineNumber, $event)"
                        @update:deletedFiles="setComponentDeletedPhotos(item.lineNumber, component.componentLineNumber, $event)"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </div>
          </div>

        <div v-if="!rmaCreated" class="action-section">
          <div class="selected-count">
            {{ selectedItems.size + selectedComponents.size }} {{ t('rma.ItemsSelected') }}
            <span v-if="selectedComponents.size > 0" class="component-count-detail">
              ({{ selectedItems.size }} items, {{ selectedComponents.size }} components)
            </span>
          </div>
          <KButton
            :theme-color="'primary'"
            @click="proceedWithReturn"
            :disabled="selectedItems.size === 0 && selectedComponents.size === 0"
            class="proceed-button"
          >
            {{ t('rma.ProceedWithReturn') }}
          </KButton>
        </div>
      </div>

      <!-- Items to Receive Section - Shows after items are selected -->
      <div v-if="rmaCreated" class="items-receive-section">
        <div class="receive-header">
          <h2>{{ t('rma.ItemsToReceive') }}</h2>
        </div>

        <div class="receive-items-list">
          <div
            v-for="(receiveItem, index) in itemsToReceive"
            :key="index"
            class="receive-item-card"
          >
            <div class="receive-item-details">
              <div class="receive-item-header">
                <h4 class="receive-item-name">{{ receiveItem.item.productName }}</h4>
                <div class="receive-item-price">{{ FormatCurrency(receiveItem.item.price ?? 0, null) }}</div>
              </div>
              <div class="receive-item-info">
                <div class="info-row">
                  <span class="rma-label">{{ t('rma.SKU') }}:</span>
                  <span class="value">{{ receiveItem.item.skuCode }}</span>
                </div>
                <div class="info-row">
                  <span class="rma-label">{{ t('rma.ProductCode') }}:</span>
                  <span class="value">{{ receiveItem.item.productCode }}</span>
                </div>
                <div class="info-row">
                  <span class="rma-label">{{ t('rma.Quantity') }}:</span>
                  <span class="value">{{ receiveItem.quantity }}</span>
                </div>
                <div class="info-row">
                  <span class="rma-label">{{ t('rma.ReturnReason') }}:</span>
                  <span class="value">{{ receiveItem.reason.label }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Address Display Section -->
        <div v-if="!showAddressSelection && !showAddressForm" class="address-display-section">
          <div class="address-display-header">
            <h3>{{ t('rma.ReturnAddress') }}</h3>
            <button v-if="selectedAddress" @click="editAddress" class="edit-address-button">
              <FontAwesomeIcon :icon="faPencil" />
              {{ t('buttons.Edit') }}
            </button>
          </div>

          <div v-if="selectedAddress" class="selected-address-card">
            <div class="address-content">
              <div class="address-name">{{ selectedAddress.firstName }} {{ selectedAddress.lastName }}</div>
              <div class="address-line">
                {{ selectedAddress.addressLine1 }}
                <span v-if="selectedAddress.houseNumber"> {{ selectedAddress.houseNumber }}</span>
              </div>
              <div v-if="selectedAddress.addressLine2" class="address-line">{{ selectedAddress.addressLine2 }}</div>
              <div class="address-line">{{ selectedAddress.city }}<template v-if="selectedAddress.stateCode && selectedAddress.stateCode !== 'N/A'">, {{ selectedAddress.stateCode }}</template> {{ selectedAddress.postalCode }}</div>
              <div class="address-line">{{ selectedAddress.countryCode }}</div>
            </div>
          </div>

          <div v-else class="no-address-selected">
            <p>{{ t('rma.NoAddressSelected') }}</p>
            <button @click="editAddress" class="select-address-button">
              {{ t('rma.SelectAddress') }}
            </button>
          </div>
        </div>

        <!-- Totals Section -->
        <div v-if="!showAddressSelection && !showAddressForm && rmaTotals" class="totals-section">
          <h3>{{ t('rma.Totals') }}</h3>
          <div class="totals-card">
            <div class="totals-row">
              <span class="rma-label">{{ t('rma.CreditAmount') }}</span>
              <span class="value">{{ FormatCurrency(rmaTotals.plannedInboundReturnTotal ?? 0, null) }}</span>
            </div>
            <div class="totals-row">
              <span class="rma-label">{{ t('rma.Fees') }}</span>
              <span class="value">{{ FormatCurrency(rmaTotals.feeTotal ?? 0, null) }}</span>
            </div>
            <div class="totals-row">
              <span class="rma-label">{{ t('rma.ReturnCreditBalance') }}</span>
              <span class="value">{{ FormatCurrency(rmaTotals.plannedBalance ?? 0, null) }}</span>
            </div>
            <div class="totals-row">
              <span class="rma-label">{{ t('rma.NewPurchases') }}</span>
              <span class="value">{{ FormatCurrency(rmaTotals.outboundTotal ?? 0, null) }}</span>
            </div>
            <div class="totals-row">
              <span class="rma-label">{{ t('rma.BuyingDiscount') }}</span>
              <span class="value">{{ FormatCurrency(rmaTotals.outboundBuyingDiscountTotal ?? 0, null) }}</span>
            </div>
            <div class="totals-row">
              <span class="rma-label">{{ t('rma.ShippingAmount') }}</span>
              <span class="value">{{ FormatCurrency(rmaTotals.shippingAmount ?? 0, null) }}</span>
            </div>
            <div class="totals-row totals-row-total">
              <span class="rma-label">{{ t('rma.Total') }}</span>
              <span class="value">{{ FormatCurrency(rmaNetTotal, null) }}</span>
            </div>
          </div>
        </div>

        <!-- New Payment Section (self-service equivalent of the admin RMA screen's
             ReturnDetailPayment control - v-if (dataItem.total > 0) branch: collecting a
             balance owed by the customer, e.g. an exchange upcharge) -->
        <div
          v-if="!showAddressSelection && !showAddressForm && paymentRequired"
          class="payment-required-section"
        >
          <h3>{{ t('rma.PaymentRequired') }}</h3>
          <p class="payment-required-description">
            {{ t('rma.PaymentRequiredDescription') }}
          </p>

          <div v-if="rmaPayments.length > 0" class="payment-list">
            <div
              v-for="payment in rmaPayments"
              :key="payment.lineNumber"
              class="payment-list-row"
            >
              <span class="payment-col-name">{{ payment.name }}</span>
              <span class="payment-col-charge">{{ FormatCurrency(getPaymentDisplayAmount(payment), null) }}</span>
              <span class="payment-col-status">{{ payment.transactionStateText }}</span>
              <span class="payment-col-action">
                <KButton
                  :svg-icon="trashIcon"
                  fill-mode="flat"
                  theme-color="error"
                  size="small"
                  @click="deleteRmaPayment(payment)"
                  :title="t('buttons.Delete')"
                />
              </span>
            </div>
          </div>

          <div v-else class="payment-gateway-panels">
            <div id="braintreepaypalbutton"></div>
            <div id="braintreepaypalbutton-placeholder" class="paypal-button-placeholder">
              <KLoader type="infinite-spinner" size="small" />
            </div>
            <KExpansionPanel
              v-for="(viewModel, index) in paymentViewModels"
              :title="viewModel.name"
              :expanded="paymentMethodsExpanded[index]"
              :key="index"
              @action="paymentExpansionPanelHandler(index)"
            >
              <!-- BankPayment (OPA) is excluded from KReveal: it loads the customer's bank
                   details asynchronously in onMounted, and its content grows once that
                   resolves. KReveal measures/locks the panel height at the moment it's
                   expanded, so if that happens while the gateway is still in its loading
                   state, the panel doesn't resize to fit the bank details until it's
                   toggled again. Matches the same exclusion in AoePaymentControl.vue. -->
              <KReveal
                v-if="viewModel.controlFolderName !== BankPaymentControlFolderName"
                :appear="paymentMethodsExpanded[index]"
              >
                <KExpansionPanelContent v-if="paymentMethodsExpanded[index]">
                  <BraintreeRmaPaymentEntry
                    v-if="viewModel.controlFolderName === BrainTreeControlFolderName"
                    :rma-number="createdRmaNumber"
                    :charge="Math.abs(rmaTotals?.plannedBalance ?? 0)"
                    :system-keyword="viewModel.systemKeyword"
                    :name="viewModel.name"
                    :check3-d-s="viewModel.parameters?.Check3DS === true || viewModel.parameters?.Check3DS === 'true'"
                  ></BraintreeRmaPaymentEntry>
                  <NotSupported
                    v-else
                    :system-keyword="viewModel.systemKeyword"
                    :name="viewModel.name"
                  ></NotSupported>
                </KExpansionPanelContent>
              </KReveal>
              <KExpansionPanelContent v-else-if="paymentMethodsExpanded[index]">
                <BankPaymentRmaEntry
                  :rma-number="createdRmaNumber"
                  :charge="Math.abs(rmaTotals?.plannedBalance ?? 0)"
                  :system-keyword="viewModel.systemKeyword"
                  :name="viewModel.name"
                ></BankPaymentRmaEntry>
              </KExpansionPanelContent>
            </KExpansionPanel>
          </div>
        </div>

        <!-- Refund Method Selection Section (self-service equivalent of the admin RMA
             screen's ReturnDetailPayment control - credit/refund branch only) -->
        <div
          v-if="!showAddressSelection && !showAddressForm && refundRequired"
          class="refund-method-section"
        >
          <h3>{{ t('rma.RefundMethod') }}</h3>
          <p class="refund-method-description">
            {{ t('rma.ReturnCreditRequired') }}
          </p>

          <div class="refund-method-option">
            <input
              type="radio"
              name="creditMethod"
              id="originalPaymentMethod"
              value="originalPaymentMethod"
              checked
              disabled
            />
            <label for="originalPaymentMethod">
              {{ t('rma.RefundOriginalPaymentMethod') }}
            </label>
          </div>

          <div class="refund-method-table">
            <div class="refund-method-table-header">
              <span class="refund-col-check"></span>
              <span class="refund-col-name">{{ t('rma.RefundColumnName') }}</span>
              <span class="refund-col-charge">{{ t('rma.RefundColumnCharge') }}</span>
            </div>
            <div
              v-for="payment in originalPaymentMethods"
              :key="payment.paymentGuid"
              class="refund-method-table-row"
            >
              <span class="refund-col-check">
                <KCheckBox
                  :checked="payment.checked"
                  :value="payment.checked"
                  :disabled="isProcessingRefundSelection"
                  @change="checkedChangeHandler(payment)"
                />
              </span>
              <span class="refund-col-name">{{ payment.name }}</span>
              <span class="refund-col-charge">{{ FormatCurrency(payment.charge, null) }}</span>
            </div>
          </div>

          <div v-if="remainingRefundToAllocate > 0" class="refund-remaining-notice">
            {{ t('rma.RefundRemainingToAllocate', { amount: FormatCurrency(remainingRefundToAllocate, null) }) }}
          </div>
        </div>

        <div v-if="!showAddressSelection && !showAddressForm" class="receive-actions">
          <KButton
            :theme-color="'secondary'"
            @click="goBackToItems"
            class="go-back-button"
          >
            {{ t('rma.GoBack') }}
          </KButton>
          <KButton
            :theme-color="'primary'"
            @click="submitRMA"
            :disabled="!selectedAddressId || isSubmittingRMA || (refundRequired && remainingRefundToAllocate > 0) || (paymentRequired && rmaPayments.length === 0)"
            class="submit-rma-button"
          >
            {{ t('rma.SubmitRMA') }}
          </KButton>
        </div>
      </div>

      <!-- Address Selection View -->
      <div v-if="showAddressSelection && !showAddressForm" class="address-selection-section">
        <div class="selection-header">
          <button @click="cancelAddressSelection" class="back-button">
            <FontAwesomeIcon :icon="faArrowLeft" />
            {{ t('buttons.Back') }}
          </button>
          <h2>{{ t('rma.SelectReturnAddress') }}</h2>
        </div>

        <div v-if="isLoadingAddresses" class="loading-message">
          {{ t('labels.Loading') }}...
        </div>

        <div v-else class="address-list">
          <div
            v-for="address in addressList"
            :key="address.addressId"
            class="address-item selectable"
            @click="selectExistingAddress(address)"
          >
            <div class="address-content">
              <div class="address-name">{{ address.firstName }} {{ address.lastName }}</div>
              <div class="address-line">
                {{ address.addressLine1 }}
                <span v-if="address.houseNumber"> {{ address.houseNumber }}</span>
              </div>
              <div v-if="address.addressLine2" class="address-line">{{ address.addressLine2 }}</div>
              <div class="address-line">{{ address.city }}<template v-if="address.stateCode && address.stateCode !== 'N/A'">, {{ address.stateCode }}</template> {{ address.postalCode }}</div>
              <div class="address-line">{{ address.countryCode }}</div>
            </div>
          </div>

          <div class="address-item add-new" @click="addNewAddress">
            <div class="add-new-content">
              <FontAwesomeIcon :icon="faPlus" class="add-icon" />
              <span>{{ t('rma.AddNewAddress') }}</span>
            </div>
          </div>
        </div>

        <div class="submit-actions">
          <KButton
            :theme-color="'primary'"
            @click="submitRMA"
            :disabled="!selectedAddressId || isSubmittingRMA"
            class="submit-rma-button"
          >
            {{ t('rma.SubmitRMA') }}
          </KButton>
        </div>
      </div>

      <!-- Address Form View -->
      <div v-if="showAddressForm" class="address-form-section">
        <div class="form-header">
          <button @click="cancelAddressForm" class="back-button">
            <FontAwesomeIcon :icon="faArrowLeft" />
            {{ t('buttons.Back') }}
          </button>
          <h2>{{ t('rma.AddNewAddress') }}</h2>
        </div>

        <AddressControl
          ref="addressControlRef"
          :name="addressNameDisplay"
          :addressLine1="addressForm.address1"
          :addressLine2="addressForm.address2"
          :houseNumber="addressForm.houseNumber"
          :city="addressForm.city"
          :stateCode="addressForm.state"
          :postalCode="addressForm.postalCode"
          :countryCode="addressForm.country"
          :columnClass="'col-12'"
          :isdisabled="false"
          @address-change="onAddressChange"
        />

        <div class="form-actions">
          <button @click="cancelAddressForm" class="form-button secondary">
            {{ t('buttons.Cancel') }}
          </button>
          <button @click="saveAddress" class="form-button primary">
            {{ t('buttons.Save') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Address Verification Popup -->
    <div v-if="addressVerificationShow" class="popup-backdrop"></div>
    <Popup
      :show="addressVerificationShow"
      :popup-class="'address-val-popup-content'"
    >
      <VerifyAddressPopup
        :enteredAddress="{
          addressLine1: addressForm.address1,
          addressLine2: addressForm.address2,
          houseNumber: addressForm.houseNumber,
          city: addressForm.city,
          stateCode: addressForm.state,
          postalCode: addressForm.postalCode,
          countryCode: addressForm.country,
          firstName: addressForm.firstName,
          lastName: addressForm.lastName
        }"
        :verifyAddress="verifyAddress"
        :verifyAddressError="addressVerifyError"
        @use-entered-address="useEnteredAddressClick"
        @use-selected-address="useSelectedAddressClick"
        @re-enter-address="reEnterAddressClick">
      </VerifyAddressPopup>
    </Popup>
  </div>
</template>

<style lang="scss" scoped>
.aoe-rma-entry {
  max-width: 1200px;
  margin: 0 auto;
  padding: $card-padding;
}

.exit-button-container {
  display: flex;
  justify-content: flex-start;
  margin-bottom: $spacing-5;
}

.exit-button {
  display: flex;
  align-items: center;
  gap: $spacing-1;
  padding: $spacing-2 $spacing-4;
  border-radius: $radius-sm;
  background-color: var(--vt-c-white-soft);
  border: 1px solid #e0e0e0;
  color: #333;
  transition: all 0.2s;
}

.exit-button:hover {
  background-color: #e0e0e0;
}

.exit-icon {
  @include typography-body-large;
}
.rma-header {
  text-align: center;
  margin-bottom: 40px;
}

.rma-header h1 {
  @include typography-h2;
  color: #2c3e50;
  margin-bottom: $spacing-2-5;
}

.rma-header p {
  @include typography-body-large;
  color: #666;
}

.order-search-section {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: $radius-md;
  padding: $spacing-8;
  margin-bottom: $spacing-8;
  box-shadow: 0 2px 4px rgba(black, 0.1);
}

.search-container {
  display: flex;
  gap: $spacing-4;
  align-items: flex-end;
  margin-bottom: $spacing-5;
}

.search-input-group {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: $gap-sm;
}

.search-input-group label {
  @include font-weight-semibold;
  color: #2c3e50;
}

.order-input {
  width: 100%;
}

.search-button {
  min-width: 120px;
}
.search-buttonx {
  border-color: var(--vt-c-teal) !important;
  background-color: var(--vt-c-teal);
}

.alert {
  padding: $spacing-4;
  border-radius: $radius-sm;
  margin-top: $spacing-4;
}

.alert-danger {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
}

/* Existing RMA Section */
.existing-rma-section {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: $radius-md;
  padding: $spacing-8;
  margin-bottom: $spacing-8;
  box-shadow: 0 2px 4px rgba(black, 0.1);
}

.existing-rma-card {
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
}

.existing-rma-icon {
  @include typography-display;
  margin-bottom: $spacing-5;
}

.existing-rma-card h2 {
  @include typography-h2;
  color: #856404;
  margin-bottom: $spacing-4;
}

.existing-rma-message {
  @include typography-body-large;
  color: #555;
  margin-bottom: $spacing-5;
}

.existing-rma-number {
  @include typography-body-large;
  padding: $spacing-4;
  background-color: #fff3cd;
  border: 2px solid #ffc107;
  border-radius: $radius-md;
  margin-bottom: $spacing-5;
  color: #856404;
}

.existing-rma-number strong {
  color: #664d03;
}

.existing-rma-info {
  @include typography-body;
  color: #666;
  font-style: italic;
}

.order-details-section {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: $radius-md;
  padding: $spacing-8;
  box-shadow: 0 2px 4px rgba(black, 0.1);
}

/* New 4-column grid layout */
.order-details-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: $spacing-5;
  margin-bottom: $spacing-8;
  padding-bottom: $spacing-8;
  border-bottom: 2px solid var(--vt-c-white-soft);
}

.order-details-grid > div {
  background: var(--vt-c-white-soft);
  padding: $card-padding;
  border-radius: $radius-md;
  border: 1px solid #e0e0e0;
}

.order-details-grid h3 {
  @include typography-body-large;
  @include font-weight-semibold;
  color: #2c3e50;
  margin: 0 0 15px 0;
  padding-bottom: $spacing-2-5;
  border-bottom: 2px solid var(--vt-c-teal);
}

.detail-item {
  @include typography-body;
  margin-bottom: 12px;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.detail-label {
  @include typography-small;
  @include font-weight-semibold;
  display: block;
  color: #666;
  margin-bottom: 4px;
}

.detail-value {
  @include font-weight-medium;
  display: block;
  color: #2c3e50;
  word-break: break-word;
}

/* Responsive design for smaller screens */
@media (max-width: 1200px) {
  .order-details-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .order-details-grid {
    grid-template-columns: 1fr;
  }
}

/* Reason Codes Section */
.reason-codes-section {
  background: var(--vt-c-white-soft);
  border: 1px solid #e0e0e0;
  border-radius: $radius-md;
  padding: $card-padding;
  margin-bottom: $spacing-8;
}

.reason-codes-section h3 {
  @include typography-body-large;
  @include font-weight-semibold;
  color: #2c3e50;
  margin: 0 0 15px 0;
  padding-bottom: $spacing-2-5;
  border-bottom: 2px solid var(--vt-c-teal);
}

.reason-code-list {
  margin-bottom: $spacing-4;
}

.reason-code-item {
  @include typography-body;
  margin-bottom: $spacing-2-5;
}

.reason-code-item strong {
  color: var(--vt-c-teal);
  display: inline-block;
  margin-right: $spacing-1;
}

.reason-code-item span {
  color: #555;
}

.reason-codes-note {
  margin-top: $spacing-4;
  padding-top: $spacing-4;
  border-top: 1px solid #e0e0e0;
}

.reason-codes-note p {
  @include typography-small;
  margin: 0;
  color: #666;
  font-style: italic;
}

.order-summary {
  margin-bottom: $spacing-8;
  padding-bottom: $spacing-5;
  border-bottom: 2px solid var(--vt-c-white-soft);
}

.order-summary h2 {
  @include typography-h3;
  color: #2c3e50;
  margin-bottom: $spacing-4;
}

.order-info {
  display: flex;
  gap: $spacing-8;
  flex-wrap: wrap;
}

.info-item {
  @include typography-body;
  color: #555;
}

.info-item strong {
  color: #2c3e50;
}

.items-selection-section h3 {
  @include typography-h4;
  color: #2c3e50;
  margin-bottom: $spacing-2-5;
}

.instruction-text {
  color: #666;
  margin-bottom: $spacing-5;
  font-style: italic;
}

.items-list {
  display: flex;
  flex-direction: column;
  gap: $spacing-4;
  margin-bottom: $spacing-8;
}

.item-wrapper {
  border: 2px solid #e0e0e0;
  border-radius: $radius-md;
  padding: $card-padding;
  background: #fefefe;
  transition: all 0.2s;
}

.item-wrapper:hover {
  border-color: var(--vt-c-teal);
  box-shadow: 0 2px 8px rgba(#007bff, 0.1);
}

.item-card {
  display: flex;
  gap: $spacing-4;
  padding: 0;
  border: none;
  border-radius: 0;
  background: transparent;
}

.item-wrapper.has-selected {
  border-color: var(--vt-c-teal);
  background: var(--vt-c-teal)1c;
}

.item-checkbox {
  display: flex;
  align-items: center;
}

.item-checkbox input[type="checkbox"] {
  width: 20px;
  height: 20px;
  cursor: pointer;
}

/* Component SKU Styles */
.components-wrapper {
  width: 100%;
  margin-top: $spacing-2-5;
  padding-top: 20px;
  border-top: 2px solid #e0e0e0;
}

.components-header {
  margin-bottom: $spacing-4;
  padding-bottom: $spacing-2-5;
  border-bottom: 1px solid #cccccc;
}

.components-label {
  @include typography-body;
  @include font-weight-semibold;
  color: var(--vt-c-teal);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.components-section {
  width: 100%;
  margin-top: 0;
}

.component-card {
  display: flex;
  gap: $spacing-4;
  padding: $spacing-4;
  margin-bottom: $spacing-2-5;
  margin-left: $spacing-5;
  border: 1px solid #cccccc;
  border-left: 4px solid #999999; /* Visual indicator for component */
  border-radius: $radius-md;
  background: var(--vt-c-white);
  transition: all 0.2s;
  width: calc(100% - 20px);
}

.component-card:hover {
  border-left-color: var(--vt-c-teal);
  background: #fefefe;
}

.component-selected {
  border-left-color: var(--vt-c-teal);
  background: #e9f5f7;
  border-color: var(--vt-c-teal);
}

.component-checkbox {
  display: flex;
  align-items: center;
}

.component-checkbox input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.component-checkbox input[type="checkbox"]:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.component-details {
  flex: 1;
}

.component-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: $spacing-2-5;
}

.component-name {
  @include typography-body;
  @include font-weight-medium;
  color: #2c3e50;
  margin: 0;
}

.component-price {
  @include typography-body;
  @include font-weight-semibold;
  color: #555;
}

.component-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: $gap-sm;
}

.component-reason-section {
  margin-top: $spacing-4;
  padding: $spacing-4;
  background: var(--vt-c-white);
  border-radius: $radius-md;
  border: 1px solid #cccccc;
}

.component-reason-dropdown {
  width: 100%;
  margin-bottom: $spacing-4;
}

.item-details {
  flex: 1;
}

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: $spacing-4;
}

.item-name {
  @include typography-body-large;
  color: #2c3e50;
  margin: 0;
}

.item-price {
  @include typography-h4;
  @include font-weight-semibold;
  color: var(--vt-c-teal);
}

.item-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: $spacing-2-5;
}

.info-row {
  @include typography-body;
  display: flex;
  gap: $gap-sm;
}

.info-row .rma-label {
  @include font-weight-semibold;
  color: #555;
}

.info-row .value {
  color: #333;
}

.quantity-row {
  align-items: center;
}

.quantity-dropdown {
  width: 100px;
}

.item-reason-section {
  margin-top: $spacing-4;
  padding-top: $spacing-4;
  border-top: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  gap: $gap-sm;
}

.reason-label {
  @include typography-body;
  @include font-weight-semibold;
  color: #2c3e50;
}

.item-reason-dropdown {
  width: 100%;
  max-width: 400px;
}

/* Exchange Item Section */
.exchange-item-section {
  margin-top: $spacing-4;
  padding: $spacing-4;
  background: var(--vt-c-white-soft);
  border-radius: $radius-md;
  border: 1px solid #e0e0e0;
}

.exchange-search {
  display: flex;
  flex-direction: column;
  gap: $gap-sm;
}

.exchange-label {
  @include typography-body;
  @include font-weight-semibold;
  color: #2c3e50;
}

.exchange-autocomplete {
  width: 100%;
}

/* Override Kendo list item styles for exchange search */
.exchange-autocomplete .k-list-item {
  padding: 0 !important;
  border: none !important;
}

.exchange-autocomplete .k-list-item:hover {
  background-color: transparent !important;
}

.exchange-search-item {
  display: flex;
  align-items: center;
  gap: $spacing-3;
  padding: $spacing-2-5 $spacing-3;
  cursor: pointer;
  transition: background-color 0.2s;
  width: 100%;
}

.exchange-search-item:hover {
  background-color: #f0f4f7;
}

.exchange-item-image {
  width: 50px;
  height: 50px;
  min-width: 50px;
  object-fit: cover;
  border-radius: $radius-sm;
  border: 1px solid #e0e0e0;
  background-color: var(--vt-c-white-soft);
  flex-shrink: 0;
}

.exchange-item-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: $spacing-1;
}

.exchange-item-name {
  @include typography-body;
  @include font-weight-semibold;
  color: #2c3e50;
}

.exchange-item-sku {
  @include typography-small;
  color: #666;
}

.exchange-item-selected {
  display: flex;
  flex-direction: column;
  gap: $spacing-3;
}

.exchange-selected-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: $spacing-2-5;
  border-bottom: 1px solid #e0e0e0;
}

.exchange-selected-header strong {
  @include typography-body;
  color: var(--vt-c-teal);
}

.remove-exchange-button {
  @include typography-small;
}

.exchange-selected-content {
  display: flex;
  align-items: center;
  gap: $spacing-4;
  padding: $spacing-2-5;
  background: white;
  border-radius: $radius-sm;
  border: 1px solid #e0e0e0;
}

.exchange-selected-image {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: $radius-sm;
  border: 1px solid #e0e0e0;
}

.exchange-selected-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: $spacing-2;
}

.exchange-selected-name {
  @include typography-body;
  @include font-weight-semibold;
  color: #2c3e50;
}

.exchange-selected-sku {
  @include typography-body;
  color: #666;
}

/* Photo Uploader Section */
.photo-uploader-section {
  margin-top: $spacing-4;
  padding: $spacing-4;
  background: var(--vt-c-white-soft);
  border-radius: $radius-md;
  border: 1px solid #e0e0e0;
}

.photo-label {
  @include typography-body;
  @include font-weight-semibold;
  display: block;
  color: #2c3e50;
  margin-bottom: $spacing-2;
}

.required-indicator {
  @include font-weight-bold;
  color: #d32f2f;
  margin-left: 4px;
}

.required-text {
  @include typography-small;
  @include font-weight-medium;
  color: #d32f2f;
  margin-left: 4px;
}

.action-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 20px;
  border-top: 2px solid var(--vt-c-white-soft);
}

.selected-count {
  @include typography-body;
  @include font-weight-semibold;
  color: #2c3e50;
}

.component-count-detail {
  @include typography-body;
  @include font-weight-regular;
  color: #666;
  margin-left: 8px;
}

.proceed-button {
  min-width: 200px;
  background-color: var(--vt-c-teal) !important;
  border-color: var(--vt-c-teal) !important;
}

/* Items to Receive Section */
.items-receive-section {
  margin-top: 40px;
  padding-top: 30px;
  border-top: 3px solid var(--vt-c-teal);
}

.receive-header {
  margin-bottom: $spacing-6;
}

.receive-header h2 {
  @include typography-h3;
  color: #2c3e50;
  margin-bottom: $spacing-2-5;
}

.rma-number-display {
  @include typography-body-large;
  color: #007bff;
  padding: $spacing-2-5;
  background-color: #f0f8ff;
  border-radius: $radius-sm;
  display: inline-block;
}

.rma-number-display strong {
  color: #2c3e50;
}

.receive-items-list {
  display: flex;
  flex-direction: column;
  gap: $spacing-5;
}

.item-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-bottom: $spacing-5;
}

.item-card {
  padding: $card-padding;
  border: 2px solid #47817e;
  border-radius: $radius-md;
  background: #f8fff9;
}

.receive-item-details {
  width: 100%;
}

.receive-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: $spacing-4;
  padding-bottom: $spacing-2-5;
  border-bottom: 1px solid #d4edda;
}

.receive-item-name {
  @include typography-body-large;
  color: #2c3e50;
  margin: 0;
}

.receive-item-price {
  @include typography-h4;
  @include font-weight-semibold;
  color: #478173;
}

.receive-item-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: $spacing-2-5;
}

.receive-actions {
  margin-top: 25px;
  padding-top: 20px;
  border-top: 2px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  gap: $spacing-2-5;
}

.go-back-button {
  min-width: 150px;
}

.continue-button,
.submit-rma-button {
  min-width: 200px;
  background-color:var(--vt-c-teal);
}

/* Address Display Section */
.address-display-section {
  margin-top: 40px;
  padding-top: 30px;
  border-top: 3px solid #478173;
}

.address-display-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: $spacing-6;
}

.address-display-header h3 {
  @include typography-h3;
  color: #2c3e50;
  margin: 0;
}

.edit-address-button {
  display: flex;
  align-items: center;
  gap: $gap-sm;
  padding: $button-padding-y $button-padding-x;
  background-color: #478173;
  color: white;
  border: none;
  @include typography-body;
  border-radius: $radius-md;
  cursor: pointer;
  transition: all 0.2s;
}

.edit-address-button:hover {
  background-color: #3a6b5f;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(#478173, 0.3);
}

.selected-address-card {
  background: #f8fff9;
  border: 2px solid #478173;
  border-radius: $radius-md;
  padding: $spacing-6;
  margin-bottom: $spacing-5;
}

.selected-address-card .address-content {
  color: #2c3e50;
}

.selected-address-card .address-name {
  @include typography-h4;
  @include font-weight-semibold;
  color: #478173;
  margin-bottom: 12px;
}

.selected-address-card .address-line {
  @include typography-body;
  line-height: 1.6;
  margin-bottom: $spacing-2;
  color: #555;
}

.no-address-selected {
  text-align: center;
  padding: $spacing-10 $spacing-5;
  background: var(--vt-c-white-soft);
  border: 2px dashed #cccccc;
  border-radius: $radius-md;
  margin-bottom: $spacing-5;
}

.no-address-selected p {
  @include typography-body-large;
  color: #666;
  margin-bottom: $spacing-5;
}

.select-address-button {
  padding: $spacing-3 $spacing-8;
  background-color: #478173;
  color: white;
  border: none;
  @include typography-body;
  border-radius: $radius-md;
  cursor: pointer;
  transition: all 0.2s;
}

.select-address-button:hover {
  background-color: #3a6b5f;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(#478173, 0.3);
}

/* Totals Section */
.totals-section {
  margin-top: $spacing-6;
}

.totals-section h3 {
  @include typography-h3;
  color: var(--color-heading);
  margin: 0 0 $spacing-4 0;
}

.totals-card {
  background: var(--color-background-soft);
  border: 1px solid var(--color-border);
  border-radius: $radius-md;
  padding: $spacing-6;
}

.totals-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: $spacing-2 0;
  @include typography-body;
  color: var(--color-text);
}

.totals-row + .totals-row {
  border-top: 1px solid var(--color-border);
}

.totals-row-total {
  @include font-weight-semibold;
  border-top: 2px solid var(--color-border-hover);
  margin-top: $spacing-2;
  padding-top: $spacing-3;
}

/* New Payment Section */
.payment-required-section {
  background: var(--color-background-soft);
  border: 1px solid var(--color-border);
  border-radius: $radius-md;
  padding: $spacing-6;
  margin-top: $spacing-6;
}

.payment-required-section h3 {
  @include typography-h4;
  margin-bottom: $spacing-3;
}

.payment-required-description {
  @include typography-body;
  color: var(--color-text);
  margin-bottom: $spacing-4;
}

.payment-list-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $spacing-3;
  padding: $spacing-2 0;
}

.payment-list-row + .payment-list-row {
  border-top: 1px solid var(--color-border);
}

.payment-col-name {
  @include font-weight-medium;
}

.payment-col-status {
  color: var(--color-text-soft, #666);
}

.payment-gateway-panels {
  #braintreepaypalbutton {
    padding-block: var(--kendo-spacing-3, 0.75rem);
    padding-inline: var(--kendo-spacing-4, 1rem);
  }

}

/* Refund Method Selection Section */
.refund-method-section {
  background: var(--color-background-soft);
  border: 1px solid var(--color-border);
  border-radius: $radius-md;
  padding: $spacing-6;
  margin-top: $spacing-6;
}

.refund-method-section h3 {
  @include typography-h4;
  margin-bottom: $spacing-3;
}

.refund-method-description {
  @include typography-body;
  color: var(--color-text);
  margin-bottom: $spacing-4;
}

.refund-method-option {
  display: flex;
  align-items: center;
  gap: $spacing-2;
  margin-bottom: $spacing-4;
}

.refund-method-table {
  display: flex;
  flex-direction: column;
}

.refund-method-table-header,
.refund-method-table-row {
  display: grid;
  grid-template-columns: 40px 2fr 1fr;
  align-items: center;
  gap: $spacing-3;
  padding: $spacing-2 0;
}

.refund-method-table-header {
  @include font-weight-semibold;
  border-bottom: 1px solid var(--color-border);
  color: var(--color-text);
}

.refund-method-table-row + .refund-method-table-row {
  border-top: 1px solid var(--color-border);
}

.refund-remaining-notice {
  @include typography-body;
  color: var(--color-warning, #b45309);
  margin-top: $spacing-4;
}

/* Address Selection Section */
.address-selection-section {
  margin-top: 40px;
  padding-top: 30px;
  border-top: 3px solid #478173;
}

.address-selection-section h2 {
  @include typography-h3;
  color: #2c3e50;
  margin-bottom: $spacing-6;
}

.address-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: $spacing-5;
}

.address-card {
  border: 2px solid #478173;
  border-radius: $radius-md;
  padding: $card-padding;
  background: white;
  transition: all 0.2s;
}

.address-card:hover {
  box-shadow: 0 4px 12px rgba(#007bff, 0.2);
  transform: translateY(-2px);
}

.address-header {
  margin-bottom: $spacing-4;
  padding-bottom: $spacing-2-5;
  border-bottom: 2px solid var(--vt-c-white-soft);
}

.address-header h3 {
  @include typography-body-large;
  color: var(--vt-c-teal);
  margin: 0;
}

.address-details {
  color: #555;
}

.address-details p {
  margin: $spacing-2 0;
  line-height: 1.5;
}

.address-details p strong {
  color: #2c3e50;
}

.submit-actions {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 2px solid #e0e0e0;
  display: flex;
  justify-content: center;
}

.submit-rma-button {
  @include typography-body-large;
  min-width: 250px;
  padding: $spacing-3 $spacing-6;
  background-color: var(--vt-c-teal) !important;
  border-radius: $button-radius-lg !important;
}
.submit-rma-button :hover {
  background-color: #3a5f68 !important;
}

@media (max-width: 768px) {
  .search-container {
    flex-direction: column;
    align-items: stretch;
  }

  .search-button {
    width: 100%;
  }

  .order-info {
    flex-direction: column;
    gap: $spacing-2-5;
  }

  .item-info,
  .receive-item-info {
    grid-template-columns: 1fr;
  }

  .action-section {
    flex-direction: column;
    gap: $spacing-4;
    align-items: stretch;
  }

  .proceed-button {
    width: 100%;
  }
}

/* Address Selection Styles */
.selection-header,
.form-header {
  display: flex;
  align-items: center;
  gap: $gap-md;
  margin-bottom: $spacing-6;
}

.back-button {
  display: flex;
  align-items: center;
  gap: $gap-sm;
  padding: $spacing-2 $spacing-4;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: $radius-md;
  @include typography-small;
  cursor: pointer;
  color: #666;
  transition: all 0.2s ease;
}

.back-button:hover {
  background-color: var(--vt-c-white-soft);
  border-color: var(--vt-c-teal);
  color: var(--vt-c-teal);
}

.loading-message {
  @include typography-body;
  text-align: center;
  padding: $spacing-10;
  color: #666;
}

.address-list {
  display: flex;
  flex-direction: column;
  gap: $gap-md;
  margin-bottom: $spacing-6;
}

.address-item {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: $radius-md;
  padding: $card-padding;
  transition: all 0.2s ease;
}

.address-item.selectable {
  cursor: pointer;
}

.address-item.selectable:hover {
  border-color: var(--vt-c-teal);
  box-shadow: 0 2px 8px rgba(black, 0.1);
}

.address-item.add-new {
  border: 2px dashed #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100px;
  cursor: pointer;
}

.address-item.add-new:hover {
  border-color: var(--vt-c-teal);
  background-color: var(--vt-c-white-soft);
}

.add-new-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $gap-sm;
  color: #666;
}

.add-icon {
  @include typography-h3;
  color: var(--vt-c-teal);
}

.address-content {
  line-height: 1.5;
}

.address-name {
  @include font-weight-semibold;
  color: #333;
  margin-bottom: 4px;
}

.address-line {
  @include typography-small;
  color: #666;
}

.address-form-section {
  margin-top: 40px;
  padding-top: 30px;
  border-top: 3px solid #478173;
}

.form-actions {
  display: flex;
  gap: $spacing-3;
  justify-content: flex-end;
  margin-top: 24px;
}

.form-button {
  padding: $spacing-3 $spacing-6;
  border: none;
  @include typography-small;
  @include font-weight-medium;
  border-radius: $radius-md;
  cursor: pointer;
  transition: all 0.2s ease;
}

.form-button.primary {
  background-color: var(--vt-c-teal);
  color: white;
}

.form-button.primary:hover {
  background-color: #3a5f68;
}

.form-button.secondary {
  background-color: var(--vt-c-white-soft);
  color: #666;
  border: 1px solid #e0e0e0;
}

.form-button.secondary:hover {
  background-color: #e9ecef;
}

.popup-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(black, 0.5);
  z-index: $z-modal-backdrop;
  backdrop-filter: blur(2px);
  overflow-y: auto; /* Allow scrolling if needed */
}

.address-val-popup-content {
  position: fixed;
  top: 50%;
  left: 50%;
  padding: $card-padding;
  transform: translate(-50%, -50%);
  z-index: $z-modal; /* Above backdrop */
  max-height: 90vh;
  overflow-y: auto;
  margin: auto;
}

/* Ensure popup scrolls into view when opened */
.popup-backdrop:has(.address-val-popup-content) {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
